import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from "react";
import "@/App.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import { BrowserRouter, Routes, Route, useNavigate as useRouterNavigate, useLocation, Link as RouterLink, Navigate as RouterNavigate } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import axios from "axios";
import { Toaster, toast } from "sonner";
import {
  Calendar, MapPin, Users, Bell, Plus, Search, Filter, Menu, X, Moon, Sun, 
  LogOut, User, Home, ChevronRight, Clock, Trash2, Edit, Eye, Check,
  Music, Briefcase, Trophy, UtensilsCrossed, Palette, Laptop, Heart, 
  GraduationCap, CalendarDays, Settings, ChevronLeft, List, Grid3X3,
  LayoutGrid, CalendarRange, Sparkles, Star, Crown, CalendarPlus, Lock, Loader2,
  Upload, ChevronLeftIcon, ChevronRightIcon, Image, Shield, CreditCard, BarChart3,
  ChevronUp, ChevronDown, Navigation
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { LoadScript, Autocomplete } from "@react-google-maps/api";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import MarkerClusterGroup from "@changey/react-leaflet-markercluster";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Tiles del mapa (CartoDB). Voyager (claro) tipo Google Maps; Dark Matter (oscuro) en gris.
const MAP_TILES_LIGHT = "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png";
const MAP_TILES_DARK = "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png";
const MAP_TILES_ATTR = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>';
const getMapTileUrl = (theme) => (theme === "dark" ? MAP_TILES_DARK : MAP_TILES_LIGHT);

// ==================== i18n (bilingüe valenciano/castellano) ====================
const LANGS = ["ca", "es"];
const DEFAULT_LANG = "ca"; // valenciano por defecto ("/" -> "/ca")

// Añade/normaliza el prefijo de idioma a una ruta interna: "/eventos" -> "/ca/eventos"
const localePath = (to, lang) => {
  if (!to || typeof to !== "string" || !to.startsWith("/")) return to;
  const rest = to.replace(/^\/(es|ca)(?=\/|$)/, "");
  return `/${lang}${rest}`;
};

// Idioma actual leído del primer segmento de la URL (robusto en cualquier componente)
const useLang = () => {
  const { pathname } = useLocation();
  const seg = pathname.split("/")[1];
  return LANGS.includes(seg) ? seg : DEFAULT_LANG;
};

// Wrappers que prefijan el idioma automáticamente (así los enlaces existentes son bilingües sin tocarlos)
const Link = ({ to, ...props }) => {
  const lang = useLang();
  return <RouterLink to={typeof to === "string" ? localePath(to, lang) : to} {...props} />;
};
const Navigate = ({ to, ...props }) => {
  const lang = useLang();
  return <RouterNavigate to={typeof to === "string" ? localePath(to, lang) : to} {...props} />;
};
const useNavigate = () => {
  const nav = useRouterNavigate();
  const lang = useLang();
  return useCallback((to, opts) => nav(typeof to === "string" ? localePath(to, lang) : to, opts), [nav, lang]);
};

// Diccionario de textos traducidos (SEO + chrome principal; se irá ampliando)
const I18N = {
  ca: {
    "seo.home.title": "Agenda d'esdeveniments de la Marina Alta",
    "seo.home.desc": "Descobreix concerts, festes, fires i plans a la Marina Alta: Dénia, Xàbia, Calp, Pego, Pedreguer i més. Agenda amb mapa i calendari.",
    "seo.town.title": "Esdeveniments a {town}",
    "seo.town.desc": "Agenda d'esdeveniments a {town} (Marina Alta): concerts, festes, fires i plans. Consulta dates, lloc i mapa.",
    "seo.mapa.title": "Mapa d'esdeveniments de la Marina Alta",
    "seo.mapa.desc": "Explora al mapa els esdeveniments de la Marina Alta per municipi.",
    "page.discover": "Descobreix esdeveniments",
    "page.discover.sub": "Troba els millors esdeveniments a prop teu",
    "town.h1": "Esdeveniments a {town}",
    "town.sub": "Agenda d'esdeveniments a {town}",
    "town.seeAll": "veure tota la Marina Alta",
    "town.byTown": "Per poble:",
    "nav.beOrganizer": "Ser Organitzador",
    "nav.notifications": "Notificacions",
    "nav.markAll": "Marca-ho tot",
    "nav.noNotifications": "No hi ha notificacions",
    "nav.createEvent": "Crear Esdeveniment",
    "nav.roleOrganizer": "Organitzador",
    "nav.roleUser": "Usuari",
    "nav.adminPanel": "Panell Admin",
    "nav.dashboard": "Tauler",
    "nav.myEvents": "Els meus esdeveniments",
    "nav.favorites": "Preferits",
    "nav.myRegistrations": "Els meus registres",
    "nav.goPremium": "Fes-te Premium",
    "nav.logout": "Tancar sessió",
    "nav.login": "Iniciar sessió",
    "nav.map": "Mapa",
    "footer.rights": "Tots els drets reservats.",
    "view.grid": "Graella",
    "view.list": "Llista",
    "view.calendar": "Calendari",
    "empty.title": "No hi ha esdeveniments disponibles",
    "empty.sub": "Prova a ajustar els filtres o torna més tard",
    "loading.events": "Carregant esdeveniments…",
    "map.noLocation": "No hi ha esdeveniments amb ubicació",
    "map.moveHint": "Mou o allunya el mapa per veure esdeveniments ací",
    "word.events": "esdeveniments",
    "filter.searchEvents": "Cerca esdeveniments...",
    "filter.search": "Cerca...",
    "filter.category": "Categoria",
    "filter.allCategories": "Totes les categories",
    "filter.date": "Data",
    "filter.dateStart": "Data d'inici",
    "filter.dateRange": "Rang de dates",
    "filter.fromHour": "Des de (hora)",
    "filter.toHour": "Fins a (hora)",
    "filter.clear": "Netejar",
    "filter.title": "Filtres",
    "filter.see": "Veure",
    "ev.back": "Tornar",
    "ev.edit": "Editar Esdeveniment",
    "ev.attendees": "Veure Assistents",
    "ev.willAttend": "Marcar que assistiré",
    "ev.saveFav": "Guardar en preferits",
    "ev.inFav": "En preferits",
    "ev.addCalendar": "Afegir a Google Calendar",
    "ev.openMaps": "Obrir en Google Maps",
    "ev.description": "Descripció",
    "ev.organizer": "Organitzador",
    "ev.seeAllEvents": "Veure tots els seus esdeveniments",
    "ev.attendeesTitle": "Assistents",
    "ev.premiumFeatures": "Funcions Premium",
    "ev.myAttendance": "La meua assistència",
    "ev.remove": "Llevar",
    "ev.willAttendShort": "Assistiré",
    "ev.willAttendMine": "Assistiré al meu esdeveniment",
    "ev.past": "Este esdeveniment ja ha passat",
    "ev.chooseSession": "Tria la teua sessió",
    "ev.signedUp": "apuntats",
    "ev.cancel": "Cancel·lar",
    "ev.full": "Complet",
    "ev.signUp": "Apuntar-me",
    "ev.loginToSession": "Inicia sessió per apuntar-te a una sessió",
    "ev.cantGo": "Ja no puc anar",
    "ev.imIn": "M'apunte!",
    "ev.loginToSignUp": "Inicia sessió per apuntar-te",
    "ev.maxPeople": "Màxim: {n} persones",
    "filter.town": "Població",
    "filter.allTowns": "Totes les poblacions",
    "map.onScreen": "{n} esdeveniments en pantalla",
    "map.withLocation": "{n} esdeveniments amb ubicació",
    "page.mapH1": "Mapa d'esdeveniments",
    "org.badge": "Per a Organitzadors",
    "org.heroTitle1": "Crea i gestiona els teus esdeveniments",
    "org.heroTitle2": "sense complicacions",
    "org.heroSub": "Pobleo et permet crear, organitzar i promocionar els teus esdeveniments de manera senzilla. Gestiona assistents, envia notificacions i molt més.",
    "org.ctaFree": "Començar Gratis",
    "org.seeEvents": "Veure Esdeveniments",
    "org.featuresH2": "Tot el que necessites com a organitzador",
    "org.f1t": "Gestió d'Esdeveniments", "org.f1d": "Crea, edita i administra tots els teus esdeveniments des d'un sol lloc",
    "org.f2t": "Control d'Assistents", "org.f2d": "Visualitza i gestiona la llista de persones apuntades als teus esdeveniments",
    "org.f3t": "Notificacions", "org.f3d": "Mantín informats els teus assistents amb notificacions automàtiques",
    "org.f4t": "Ubicació amb Mapa", "org.f4d": "Afig la ubicació exacta dels teus esdeveniments amb mapa interactiu",
    "org.f5t": "Tauler Complet", "org.f5d": "Estadístiques i resum de tota la teua activitat com a organitzador",
    "org.f6t": "Visibilitat", "org.f6d": "Els teus esdeveniments apareixeran al llistat públic per a tots els usuaris",
    "org.howH2": "Com funciona?",
    "org.s1t": "Registra't", "org.s1d": "Crea el teu compte gratis amb Google en segons",
    "org.s2t": "Crea el teu esdeveniment", "org.s2d": "Afig tots els detalls: data, lloc, descripció",
    "org.s3t": "Gestiona", "org.s3d": "Controla assistents i envia notificacions",
    "org.ctaH2": "A punt per començar?",
    "org.ctaSub": "Uneix-te a milers d'organitzadors que ja confien en Pobleo",
    "org.ctaBtn": "Crear el meu compte gratis",
    "prem.title": "Pobleo Premium",
    "prem.sub": "Desbloqueja totes les funcions i millora la teua experiència",
    "prem.perMonth": "/mes",
    "prem.f1t": "Guardar Preferits", "prem.f1d": "Guarda esdeveniments i accedeix-hi ràpidament",
    "prem.f2t": "Google Calendar", "prem.f2d": "Afig esdeveniments directament al teu calendari",
    "prem.f3t": "Notificacions Prioritàries", "prem.f3d": "Rep alertes abans que ningú",
    "prem.f4t": "Distintiu Premium", "prem.f4d": "Destaca amb la teua insígnia Premium",
    "prem.whatH2": "Què inclou Premium?",
    "prem.soonH3": "Pròximament",
    "prem.soonSub": "La subscripció Premium estarà disponible prompte. Estigues atent!",
    "prem.soonBtn": "Disponible Prompte",
    "form.editTitle": "Editar Esdeveniment", "form.createTitle": "Crear Nou Esdeveniment",
    "form.editSub": "Modifica els detalls del teu esdeveniment", "form.createSub": "Completa les dades per crear el teu esdeveniment",
    "form.title": "Títol *", "form.desc": "Descripció",
    "form.selectCategory": "Selecciona categoria",
    "form.startDate": "Data d'inici *", "form.hour": "Hora *",
    "form.searchLocation": "Cerca ubicació", "form.searchHint": "Escriu per buscar i selecciona dels suggeriments",
    "form.placeName": "Nom del lloc", "form.placeNamePh": "Ex: Parc del Retir",
    "form.address": "Adreça", "form.addressPh": "Carrer i número",
    "form.townHint": "Es detecta automàticament del lloc seleccionat",
    "form.lat": "Latitud", "form.lng": "Longitud",
    "form.maxAttendees": "Màxim d'assistents", "form.noLimit": "Sense límit",
    "form.images": "Imatges de l'esdeveniment", "form.uploading": "Pujant...", "form.uploadImages": "Pujar imatges",
    "form.imagesCount": "{n} imatge(s)", "form.main": "Principal",
    "form.recurring": "Esdeveniment recurrent", "form.recurringSub": "Es repeteix en múltiples dates",
    "form.recurrenceType": "Tipus de recurrència",
    "form.continuous": "Esdeveniment continu", "form.continuousD": "Curs, taller. Una inscripció per a totes les sessions",
    "form.multiple": "Múltiples passes", "form.multipleD": "Teatre, cine. Cada data té assistents independents",
    "form.weekdays": "Dies de la setmana", "form.eventHour": "Hora de l'esdeveniment",
    "form.lastDate": "Última data amb sessions", "form.selectLastDate": "Seleccionar última data",
    "form.important": "Important:", "form.importantD": "Inclou l'últim dia que hi haurà sessió. Ex: Si hi ha passes ds. 28 i dg. 29, posa com a data fi el 29 o posterior.",
    "form.public": "Esdeveniment públic", "form.publicSub": "Visible per a tots els usuaris",
    "form.saveChanges": "Guardar Canvis", "form.saving": "Guardant...",
    "day.mon": "Dl", "day.tue": "Dt", "day.wed": "Dc", "day.thu": "Dj", "day.fri": "Dv", "day.sat": "Ds", "day.sun": "Dg",
    "form.eventData": "Dades de l'esdeveniment", "form.locationTitle": "Ubicació",
    "form.placeholderTitle": "Nom de l'esdeveniment", "form.placeholderDesc": "Descriu el teu esdeveniment...",
    "form.searchPh": "Cerca un lloc o adreça...", "form.townFilled": "S'omple en triar un lloc",
    "dash.hello": "Hola, {name}!", "dash.summary": "Ací tens un resum de la teua activitat com a organitzador",
    "dash.created": "Esdeveniments Creats", "dash.attendances": "Assistències", "dash.upcoming": "Pròxims Esdeveniments",
    "dash.recentEvents": "Els meus esdeveniments recents", "dash.seeAll": "Veure tots",
    "dash.noEvents": "Encara no has creat cap esdeveniment", "dash.createFirst": "Crear el teu primer esdeveniment",
    "list.noEvents": "No tens esdeveniments", "list.createFirstSub": "Crea el teu primer esdeveniment i comença a compartir",
    "fav.none": "No tens preferits", "fav.noneSub": "Explora esdeveniments i guarda'ls en preferits", "fav.explore": "Explorar Esdeveniments",
    "reg.noneSub": "Explora esdeveniments i registra't en els que t'interessen", "list.newEvent": "Nou Esdeveniment", "reg.none": "No tens registres",
    "fav.premiumTitle": "Funció Premium", "fav.premiumSub": "Guarda els teus esdeveniments preferits i accedeix-hi ràpidament amb Premium", "fav.myTitle": "Els meus preferits",
    "att.of": "Assistents de {title}", "att.totalReg": "{n} inscripcions en total", "att.people": "{n} persones apuntades",
    "att.filterSession": "Filtrar per sessió", "att.allSessions": "Totes les sessions",
    "att.noneYet": "Encara no hi ha ningú apuntat", "att.noneSession": "No hi ha assistents en esta sessió",
    "op.published": "{n} esdeveniments publicats", "op.noEvents": "No hi ha esdeveniments en este període", "op.adjustFilters": "Ajusta els filtres per veure més esdeveniments",
  },
  es: {
    "seo.home.title": "Agenda de eventos de la Marina Alta",
    "seo.home.desc": "Descubre conciertos, fiestas, ferias y planes en la Marina Alta: Dénia, Xàbia, Calp, Pego, Pedreguer y más. Agenda con mapa y calendario.",
    "seo.town.title": "Eventos en {town}",
    "seo.town.desc": "Agenda de eventos en {town} (Marina Alta): conciertos, fiestas, ferias y planes. Consulta fechas, lugar y mapa.",
    "seo.mapa.title": "Mapa de eventos de la Marina Alta",
    "seo.mapa.desc": "Explora en el mapa los eventos de la Marina Alta por municipio.",
    "page.discover": "Descubre Eventos",
    "page.discover.sub": "Encuentra los mejores eventos cerca de ti",
    "town.h1": "Eventos en {town}",
    "town.sub": "Agenda de eventos en {town}",
    "town.seeAll": "ver toda la Marina Alta",
    "town.byTown": "Por pueblo:",
    "nav.beOrganizer": "Ser Organizador",
    "nav.notifications": "Notificaciones",
    "nav.markAll": "Marcar todo",
    "nav.noNotifications": "No hay notificaciones",
    "nav.createEvent": "Crear Evento",
    "nav.roleOrganizer": "Organizador",
    "nav.roleUser": "Usuario",
    "nav.adminPanel": "Panel Admin",
    "nav.dashboard": "Panel",
    "nav.myEvents": "Mis Eventos",
    "nav.favorites": "Favoritos",
    "nav.myRegistrations": "Mis Registros",
    "nav.goPremium": "Hazte Premium",
    "nav.logout": "Cerrar Sesión",
    "nav.login": "Iniciar Sesión",
    "nav.map": "Mapa",
    "footer.rights": "Todos los derechos reservados.",
    "view.grid": "Cuadrícula",
    "view.list": "Lista",
    "view.calendar": "Calendario",
    "empty.title": "No hay eventos disponibles",
    "empty.sub": "Intenta ajustar los filtros o vuelve más tarde",
    "loading.events": "Cargando eventos…",
    "map.noLocation": "No hay eventos con ubicación",
    "map.moveHint": "Mueve o aleja el mapa para ver eventos aquí",
    "word.events": "eventos",
    "filter.searchEvents": "Buscar eventos...",
    "filter.search": "Buscar...",
    "filter.category": "Categoría",
    "filter.allCategories": "Todas las categorías",
    "filter.date": "Fecha",
    "filter.dateStart": "Fecha inicio",
    "filter.dateRange": "Rango de fechas",
    "filter.fromHour": "Desde (hora)",
    "filter.toHour": "Hasta (hora)",
    "filter.clear": "Limpiar",
    "filter.title": "Filtros",
    "filter.see": "Ver",
    "ev.back": "Volver",
    "ev.edit": "Editar Evento",
    "ev.attendees": "Ver Asistentes",
    "ev.willAttend": "Marcar que asistiré",
    "ev.saveFav": "Guardar en favoritos",
    "ev.inFav": "En favoritos",
    "ev.addCalendar": "Añadir a Google Calendar",
    "ev.openMaps": "Abrir en Google Maps",
    "ev.description": "Descripción",
    "ev.organizer": "Organizador",
    "ev.seeAllEvents": "Ver todos sus eventos",
    "ev.attendeesTitle": "Asistentes",
    "ev.premiumFeatures": "Funciones Premium",
    "ev.myAttendance": "Mi asistencia",
    "ev.remove": "Quitar",
    "ev.willAttendShort": "Asistiré",
    "ev.willAttendMine": "Asistiré a mi evento",
    "ev.past": "Este evento ya ha pasado",
    "ev.chooseSession": "Elige tu sesión",
    "ev.signedUp": "apuntados",
    "ev.cancel": "Cancelar",
    "ev.full": "Completo",
    "ev.signUp": "Apuntarme",
    "ev.loginToSession": "Inicia sesión para apuntarte a una sesión",
    "ev.cantGo": "Ya no puedo ir",
    "ev.imIn": "¡Me apunto!",
    "ev.loginToSignUp": "Inicia sesión para apuntarte",
    "ev.maxPeople": "Máximo: {n} personas",
    "filter.town": "Población",
    "filter.allTowns": "Todas las poblaciones",
    "map.onScreen": "{n} eventos en pantalla",
    "map.withLocation": "{n} eventos con ubicación",
    "page.mapH1": "Mapa de eventos",
    "org.badge": "Para Organizadores",
    "org.heroTitle1": "Crea y gestiona tus eventos",
    "org.heroTitle2": "sin complicaciones",
    "org.heroSub": "Pobleo te permite crear, organizar y promocionar tus eventos de manera sencilla. Gestiona asistentes, envía notificaciones y mucho más.",
    "org.ctaFree": "Comenzar Gratis",
    "org.seeEvents": "Ver Eventos",
    "org.featuresH2": "Todo lo que necesitas como organizador",
    "org.f1t": "Gestión de Eventos", "org.f1d": "Crea, edita y administra todos tus eventos desde un solo lugar",
    "org.f2t": "Control de Asistentes", "org.f2d": "Visualiza y gestiona la lista de personas apuntadas a tus eventos",
    "org.f3t": "Notificaciones", "org.f3d": "Mantén informados a tus asistentes con notificaciones automáticas",
    "org.f4t": "Ubicación con Mapa", "org.f4d": "Añade la ubicación exacta de tus eventos con mapa interactivo",
    "org.f5t": "Dashboard Completo", "org.f5d": "Estadísticas y resumen de toda tu actividad como organizador",
    "org.f6t": "Visibilidad", "org.f6d": "Tus eventos aparecerán en el listado público para todos los usuarios",
    "org.howH2": "¿Cómo funciona?",
    "org.s1t": "Regístrate", "org.s1d": "Crea tu cuenta gratis con Google en segundos",
    "org.s2t": "Crea tu evento", "org.s2d": "Añade todos los detalles: fecha, lugar, descripción",
    "org.s3t": "Gestiona", "org.s3d": "Controla asistentes y envía notificaciones",
    "org.ctaH2": "¿Listo para empezar?",
    "org.ctaSub": "Únete a miles de organizadores que ya confían en Pobleo",
    "org.ctaBtn": "Crear mi cuenta gratis",
    "prem.title": "Pobleo Premium",
    "prem.sub": "Desbloquea todas las funciones y mejora tu experiencia",
    "prem.perMonth": "/mes",
    "prem.f1t": "Guardar Favoritos", "prem.f1d": "Guarda eventos y accede a ellos rápidamente",
    "prem.f2t": "Google Calendar", "prem.f2d": "Añade eventos directamente a tu calendario",
    "prem.f3t": "Notificaciones Prioritarias", "prem.f3d": "Recibe alertas antes que nadie",
    "prem.f4t": "Badge Premium", "prem.f4d": "Destaca con tu insignia Premium",
    "prem.whatH2": "¿Qué incluye Premium?",
    "prem.soonH3": "Próximamente",
    "prem.soonSub": "La suscripción Premium estará disponible pronto. ¡Mantente atento!",
    "prem.soonBtn": "Disponible Pronto",
    "form.editTitle": "Editar Evento", "form.createTitle": "Crear Nuevo Evento",
    "form.editSub": "Modifica los detalles de tu evento", "form.createSub": "Completa los datos para crear tu evento",
    "form.title": "Título *", "form.desc": "Descripción",
    "form.selectCategory": "Selecciona categoría",
    "form.startDate": "Fecha de inicio *", "form.hour": "Hora *",
    "form.searchLocation": "Buscar ubicación", "form.searchHint": "Escribe para buscar y selecciona de las sugerencias",
    "form.placeName": "Nombre del lugar", "form.placeNamePh": "Ej: Parque del Retiro",
    "form.address": "Dirección", "form.addressPh": "Calle y número",
    "form.townHint": "Se detecta automáticamente del lugar seleccionado",
    "form.lat": "Latitud", "form.lng": "Longitud",
    "form.maxAttendees": "Máximo de asistentes", "form.noLimit": "Sin límite",
    "form.images": "Imágenes del evento", "form.uploading": "Subiendo...", "form.uploadImages": "Subir imágenes",
    "form.imagesCount": "{n} imagen(es)", "form.main": "Principal",
    "form.recurring": "Evento recurrente", "form.recurringSub": "Se repite en múltiples fechas",
    "form.recurrenceType": "Tipo de recurrencia",
    "form.continuous": "Evento continuo", "form.continuousD": "Curso, taller. Una inscripción para todas las sesiones",
    "form.multiple": "Múltiples pases", "form.multipleD": "Teatro, cine. Cada fecha tiene asistentes independientes",
    "form.weekdays": "Días de la semana", "form.eventHour": "Hora del evento",
    "form.lastDate": "Última fecha con sesiones", "form.selectLastDate": "Seleccionar última fecha",
    "form.important": "Importante:", "form.importantD": "Incluye el último día que habrá sesión. Ej: Si hay pases sáb 28 y dom 29, pon como fecha fin el 29 o posterior.",
    "form.public": "Evento público", "form.publicSub": "Visible para todos los usuarios",
    "form.saveChanges": "Guardar Cambios", "form.saving": "Guardando...",
    "day.mon": "Lun", "day.tue": "Mar", "day.wed": "Mié", "day.thu": "Jue", "day.fri": "Vie", "day.sat": "Sáb", "day.sun": "Dom",
    "form.eventData": "Datos del evento", "form.locationTitle": "Ubicación",
    "form.placeholderTitle": "Nombre del evento", "form.placeholderDesc": "Describe tu evento...",
    "form.searchPh": "Busca un lugar o dirección...", "form.townFilled": "Se rellena al elegir un lugar",
    "dash.hello": "¡Hola, {name}!", "dash.summary": "Aquí tienes un resumen de tu actividad como organizador",
    "dash.created": "Eventos Creados", "dash.attendances": "Asistencias", "dash.upcoming": "Próximos Eventos",
    "dash.recentEvents": "Mis Eventos Recientes", "dash.seeAll": "Ver todos",
    "dash.noEvents": "No has creado ningún evento todavía", "dash.createFirst": "Crear tu primer evento",
    "list.noEvents": "No tienes eventos", "list.createFirstSub": "Crea tu primer evento y empieza a compartir",
    "fav.none": "No tienes favoritos", "fav.noneSub": "Explora eventos y guárdalos en favoritos", "fav.explore": "Explorar Eventos",
    "reg.noneSub": "Explora eventos y regístrate en los que te interesen", "list.newEvent": "Nuevo Evento", "reg.none": "No tienes registros",
    "fav.premiumTitle": "Función Premium", "fav.premiumSub": "Guarda tus eventos favoritos y accede a ellos rápidamente con Premium", "fav.myTitle": "Mis Favoritos",
    "att.of": "Asistentes de {title}", "att.totalReg": "{n} inscripciones en total", "att.people": "{n} personas apuntadas",
    "att.filterSession": "Filtrar por sesión", "att.allSessions": "Todas las sesiones",
    "att.noneYet": "Aún no hay nadie apuntado", "att.noneSession": "No hay asistentes en esta sesión",
    "op.published": "{n} eventos publicados", "op.noEvents": "No hay eventos en este período", "op.adjustFilters": "Ajusta los filtros para ver más eventos",
  },
};
const useT = () => {
  const lang = useLang();
  return (key, vars) => {
    let s = (I18N[lang] && I18N[lang][key]) || I18N.es[key] || key;
    if (vars) Object.keys(vars).forEach((k) => { s = s.split(`{${k}}`).join(vars[k]); });
    return s;
  };
};

// Selector de idioma (navegación cruda: cambia el prefijo manteniendo la ruta)
const LanguageSwitcher = ({ className = "" }) => {
  const location = useLocation();
  const nav = useRouterNavigate();
  const lang = useLang();
  const go = (target) => {
    const rest = location.pathname.replace(/^\/(es|ca)(?=\/|$)/, "");
    nav(`/${target}${rest}${location.search}`);
  };
  return (
    <div className={`flex items-center gap-1 text-xs font-semibold ${className}`}>
      <button type="button" onClick={() => go("ca")} className={lang === "ca" ? "text-primary" : "text-muted-foreground hover:text-foreground"} aria-label="Valencià">VAL</button>
      <span className="text-muted-foreground">·</span>
      <button type="button" onClick={() => go("es")} className={lang === "es" ? "text-primary" : "text-muted-foreground hover:text-foreground"} aria-label="Castellano">ESP</button>
    </div>
  );
};

// ==================== SEO (metadatos por página) ====================
const SITE_URL = "https://pobleo.es";
// Pinta title/description/canonical/OG/Twitter y, opcionalmente, datos estructurados (JSON-LD).
// Nota: react-helmet actualiza el <head> en cliente; Google (que ejecuta JS) lo lee. Para que los
// scrapers de redes (que NO ejecutan JS) vean el OG por evento hará falta prerender/SSR (Fase 1).
const Seo = ({ title, description, path, image, jsonLd, noindex }) => {
  const lang = useLang();
  // `path` (si se pasa) va SIN prefijo de idioma; si no, se usa la URL actual quitándole el prefijo
  const raw = path || (typeof window !== "undefined" ? window.location.pathname : "/");
  const rest = raw.replace(/^\/(es|ca)(?=\/|$)/, "");
  const canonical = `${SITE_URL}/${lang}${rest}`;
  const altHref = (l) => `${SITE_URL}/${l}${rest}`;
  const fullTitle = title ? `${title} · Pobleo` : "Pobleo · Agenda de eventos de la Marina Alta";
  const desc = description || "Conciertos, fiestas, ferias y planes en la Marina Alta. Agenda de eventos con mapa y calendario.";
  const img = image || `${SITE_URL}/logo.png`;
  const ogLocale = lang === "es" ? "es_ES" : "ca_ES";
  const ogAlt = lang === "es" ? "ca_ES" : "es_ES";
  return (
    <Helmet>
      <html lang={lang} />
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={canonical} />
      <link rel="alternate" hrefLang="ca" href={altHref("ca")} />
      <link rel="alternate" hrefLang="es" href={altHref("es")} />
      <link rel="alternate" hrefLang="x-default" href={altHref(DEFAULT_LANG)} />
      {noindex && <meta name="robots" content="noindex,follow" />}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={img} />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:locale:alternate" content={ogAlt} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />
      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </Helmet>
  );
};
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const GOOGLE_LIBRARIES = ["places"];

// Configure axios defaults
axios.defaults.withCredentials = true;

// ==================== CONTEXTS ====================

const AuthContext = createContext(null);
const ThemeContext = createContext(null);

export const useAuth = () => useContext(AuthContext);
export const useTheme = () => useContext(ThemeContext);

// ==================== THEME PROVIDER ====================

const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem("theme");
    return saved || "light";
  });

  useEffect(() => {
    localStorage.setItem("theme", theme);
    if (theme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [theme]);

  const toggleTheme = () => setTheme(theme === "light" ? "dark" : "light");

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// ==================== AUTH PROVIDER ====================

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = (returnTo = '/') => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = returnTo;
    // En local: saltar Google OAuth y usar el login de desarrollo (requiere DEV_AUTH=1 en el backend).
    // Entra directamente como admin. En producción este branch NO se ejecuta.
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (isLocal) {
      window.location.href = `${BACKEND_URL}/auth/login?redirect=${encodeURIComponent(window.location.origin + returnTo)}`;
      return;
    }
    // Redirect to Google OAuth endpoint
    window.location.href = `${BACKEND_URL}/auth/google/login?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
      setUser(null);
      toast.success("Sesión cerrada");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const becomeOrganizer = async () => {
    try {
      await axios.post(`${API}/user/become-organizer`);
      setUser({ ...user, role: "organizer" });
      toast.success("¡Ahora eres organizador!");
    } catch (error) {
      console.error("Error becoming organizer:", error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, checkAuth, becomeOrganizer }}>
      {children}
    </AuthContext.Provider>
  );
};

// ==================== AUTH CALLBACK ====================

const AuthCallback = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionId = new URLSearchParams(hash.substring(1)).get("session_id");

      if (!sessionId) {
        navigate("/");
        return;
      }

      try {
        const response = await axios.post(`${API}/auth/session`, { session_id: sessionId });
        setUser(response.data);
        toast.success("¡Bienvenido!");
        navigate("/dashboard", { replace: true, state: { user: response.data } });
      } catch (error) {
        console.error("Auth error:", error);
        toast.error("Error de autenticación");
        navigate("/");
      }
    };

    processAuth();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center animate-fadeIn">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-muted-foreground">Iniciando sesión...</p>
      </div>
    </div>
  );
};

// ==================== PROTECTED ROUTE ====================

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (location.state?.user || user) {
    return children;
  }

  return <Navigate to="/organizador" replace />;
};

// ==================== CATEGORY ICONS ====================

const categoryIcons = {
  music: Music,
  business: Briefcase,
  sports: Trophy,
  food: UtensilsCrossed,
  arts: Palette,
  technology: Laptop,
  health: Heart,
  education: GraduationCap,
  social: Users,
  general: CalendarDays
};

const getCategoryIcon = (category) => {
  const Icon = categoryIcons[category] || CalendarDays;
  return Icon;
};

const categoryColors = {
  music: "bg-gradient-to-br from-orange-400 to-orange-600",
  business: "bg-gradient-to-br from-sky-500 to-blue-600",
  sports: "bg-gradient-to-br from-emerald-400 to-teal-600",
  food: "bg-gradient-to-br from-amber-400 to-orange-500",
  arts: "bg-gradient-to-br from-rose-400 to-pink-600",
  technology: "bg-gradient-to-br from-cyan-400 to-sky-600",
  health: "bg-gradient-to-br from-red-400 to-rose-600",
  education: "bg-gradient-to-br from-yellow-400 to-amber-500",
  social: "bg-gradient-to-br from-blue-400 to-indigo-600",
  general: "bg-gradient-to-br from-slate-400 to-slate-600"
};

const categoryNames = {
  music: "Música",
  business: "Negocis",
  sports: "Esports",
  food: "Gastronomia",
  arts: "Art i Cultura",
  technology: "Tecnologia",
  health: "Salut i Benestar",
  education: "Educació",
  social: "Social",
  general: "General"
};

// Nombres de categorías personalizadas (id -> nombre), poblado al cargar las categorías de la BD.
// Permite mostrar el nombre real de categorías creadas en el admin, no su id.
const dynamicCategoryNames = {};
const registerCategories = (list) => {
  (list || []).forEach((c) => { if (c && c.id) dynamicCategoryNames[c.id] = c.name; });
  return list;
};
// Devuelve el nombre legible de una categoría: 1º mapa base, 2º categorías de la BD, 3º el propio id.
const getCategoryName = (id) => categoryNames[id] || dynamicCategoryNames[id] || id;

// Color de texto por categoría (para resaltar la hora sin fondo)
const categoryTextColors = {
  music: "text-orange-600",
  business: "text-blue-600",
  sports: "text-teal-600",
  food: "text-orange-500",
  arts: "text-pink-600",
  technology: "text-sky-600",
  health: "text-rose-600",
  education: "text-amber-600",
  social: "text-indigo-600",
  general: "text-slate-600"
};

// Imagen por defecto por categoría (se usa cuando el evento no tiene image_url)
const defaultImages = {
  music: "https://images.unsplash.com/photo-1470225620780-dba8ba36b745?w=800",
  business: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=800",
  sports: "https://images.unsplash.com/photo-1461896836934-ffe607ba8211?w=800",
  food: "https://images.unsplash.com/photo-1504674900941-0026722cea53?w=800",
  arts: "https://images.unsplash.com/photo-1561214115-6d2f1b0609fa?w=800",
  technology: "https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=800",
  health: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=800",
  education: "https://images.unsplash.com/photo-1507842217343-583f20270319?w=800",
  social: "https://images.unsplash.com/photo-1519671482677-1f81a1274a0a?w=800",
  general: "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=800",
  // Categorías de fiestas populares (Marina Alta)
  culture: "https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=800",
  popular: "https://images.unsplash.com/photo-1535958636474-b021ee887b13?w=800",
  sport: "https://images.unsplash.com/photo-1461896836934-ffe607ba8211?w=800",
  kids: "https://images.unsplash.com/photo-1526634332515-d56c5fd16991?w=800",
  fire: "https://images.unsplash.com/photo-1467810563316-b5476525c0f9?w=800"
};

// Devuelve la imagen del evento o, si no tiene, una por defecto según su categoría
const getEventImage = (event) =>
  event?.image_url || defaultImages[event?.category] || defaultImages.general;

// Quita acentos para comparar nombres (Castelló vs Castellon, etc.)
const stripAccents = (s) => (s || "").normalize("NFD").replace(/[̀-ͯ]/g, "");

// Provincias (para detectar que la población es el segmento anterior en la dirección)
const PROVINCE_NAMES = ["alicante", "alacant", "valencia", "castellon", "castello"];

// Devuelve la POBLACIÓN (municipio) de un evento.
// 1º usa el campo `municipality` (resuelto/verificado en el backend al importar).
// 2º, si falta, intenta derivarla de la dirección SOLO si es fiable (hay provincia);
//     en caso contrario devuelve null para no mostrar direcciones como si fueran poblaciones.
const getEventTown = (event) => {
  const muni = (event?.municipality || "").trim();
  if (muni) return muni;

  const addr = (event?.location_address || "").trim();
  if (!addr) return null;
  let parts = addr.split(",").map((s) => s.trim()).filter(Boolean);
  parts = parts.filter((p) => !/^(espa(ñ|n)a|spain)$/i.test(p)); // quitar país
  parts = parts.filter((p) => !/^\d{4,5}$/.test(p)); // quitar código postal suelto
  parts = parts.map((p) => p.replace(/^\d{4,5}\s+/, "").trim()); // "03750 Pego" -> "Pego"
  const provIdx = parts.findIndex((p) => PROVINCE_NAMES.includes(stripAccents(p).toLowerCase()));
  if (provIdx > 0) return parts[provIdx - 1]; // población = segmento antes de la provincia
  return null; // sin provincia identificable: no arriesgar (evita mostrar direcciones)
};

// Slug de una población para las URLs de pueblo: "Dénia" -> "denia", "Gata de Gorgos" -> "gata-de-gorgos"
const slugifyTown = (name) =>
  stripAccents(name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

// ==================== HEADER ====================

const Header = () => {
  const { user, login, logout, becomeOrganizer } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Helper to check if route is active
  const isActive = (path) => {
    if (path === '/') return location.pathname === '/' || location.pathname === '/eventos';
    return location.pathname.startsWith(path);
  };

  useEffect(() => {
    if (user) {
      fetchNotifications();
      fetchUnreadCount();
    }
  }, [user]);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`);
      setNotifications(response.data);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/notifications/unread-count`);
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error("Error fetching unread count:", error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`);
      setNotifications(notifications.map(n => 
        n.notification_id === notificationId ? { ...n, is_read: true } : n
      ));
      setUnreadCount(Math.max(0, unreadCount - 1));
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`);
      setNotifications(notifications.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  const isOrganizer = user?.role === "organizer";
  const isAdmin = user?.role === "admin";

  return (
    <header className="sticky top-0 z-50 glass border-b" data-testid="header">
      <div className="container mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo">
            <img src="/logo.png" alt="Pobleo" className="w-10 h-10 rounded-xl object-cover shadow-md" />
            <span className="font-bold text-xl tracking-tight hidden sm:block bg-gradient-to-r from-sky-600 to-orange-500 bg-clip-text text-transparent">Pobleo</span>
          </Link>

          {/* Center Navigation - Public */}
          <nav className="hidden md:flex items-center gap-1">
            {!user && (
              <Link
                to="/organizador"
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  isActive('/organizador')
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
                data-testid="nav-organizador"
              >
                <Sparkles className="w-4 h-4" />
                {t("nav.beOrganizer")}
              </Link>
            )}
          </nav>

          {/* Right Side */}
          <div className="flex items-center gap-2">
            {/* Selector de idioma */}
            <LanguageSwitcher className="mr-1" />

            {/* Theme Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="rounded-full"
              data-testid="theme-toggle"
            >
              {theme === "light" ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
            </Button>

            {/* Favorites - Only for authenticated users */}
            {user && (
              <Link to="/favoritos">
                <Button
                  variant={isActive('/favoritos') ? "default" : "ghost"}
                  size="icon"
                  className="rounded-full relative"
                  data-testid="favorites-btn"
                >
                  <Star className="w-5 h-5" />
                  {!user.is_premium && (
                    <Lock className="absolute -top-1 -right-1 w-3 h-3 text-amber-500" />
                  )}
                </Button>
              </Link>
            )}

            {user ? (
              <>
                {/* Premium Badge */}
                {user.is_premium && (
                  <Badge variant="secondary" className="hidden sm:flex items-center gap-1 bg-amber-500/10 text-amber-600 border-amber-500/20">
                    <Crown className="w-3 h-3" />
                    Premium
                  </Badge>
                )}

                {/* Notifications */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="rounded-full relative" data-testid="notifications-btn">
                      <Bell className="w-5 h-5" />
                      {unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 w-5 h-5 bg-accent text-accent-foreground text-xs rounded-full flex items-center justify-center">
                          {unreadCount}
                        </span>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-80">
                    <div className="flex items-center justify-between px-4 py-2 border-b">
                      <span className="font-semibold">{t("nav.notifications")}</span>
                      {unreadCount > 0 && (
                        <Button variant="ghost" size="sm" onClick={markAllAsRead} data-testid="mark-all-read">
                          {t("nav.markAll")}
                        </Button>
                      )}
                    </div>
                    <ScrollArea className="h-[300px]">
                      {notifications.length === 0 ? (
                        <p className="text-center text-muted-foreground py-8">{t("nav.noNotifications")}</p>
                      ) : (
                        notifications.map((notification) => (
                          <div
                            key={notification.notification_id}
                            className={`px-4 py-3 border-b last:border-0 cursor-pointer hover:bg-muted/50 ${!notification.is_read ? 'bg-primary/5' : ''}`}
                            onClick={() => markAsRead(notification.notification_id)}
                            data-testid={`notification-${notification.notification_id}`}
                          >
                            <p className="font-medium text-sm">{notification.title}</p>
                            <p className="text-xs text-muted-foreground mt-1">{notification.message}</p>
                          </div>
                        ))
                      )}
                    </ScrollArea>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Create Event Button - Only for organizers */}
                {isOrganizer ? (
                  <Button
                    onClick={() => navigate("/create-event")}
                    className="hidden sm:flex items-center gap-2 rounded-full"
                    data-testid="create-event-btn"
                  >
                    <Plus className="w-4 h-4" />
                    {t("nav.createEvent")}
                  </Button>
                ) : (
                  <Button
                    onClick={becomeOrganizer}
                    variant="outline"
                    className="hidden sm:flex items-center gap-2 rounded-full"
                    data-testid="become-organizer-btn"
                  >
                    <Sparkles className="w-4 h-4" />
                    {t("nav.beOrganizer")}
                  </Button>
                )}

                {/* User Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="rounded-full p-0" data-testid="user-menu">
                      <Avatar className="w-9 h-9">
                        <AvatarImage src={user.picture} alt={user.name} />
                        <AvatarFallback>{user.name?.charAt(0)}</AvatarFallback>
                      </Avatar>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-4 py-2 border-b">
                      <p className="font-medium">{user.name}</p>
                      <p className="text-xs text-muted-foreground">{user.email}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="secondary" className="text-xs capitalize">
                          {user.role === "organizer" ? t("nav.roleOrganizer") : t("nav.roleUser")}
                        </Badge>
                        {user.is_premium && (
                          <Badge className="text-xs bg-amber-500 hover:bg-amber-600">Premium</Badge>
                        )}
                      </div>
                    </div>
                    {isAdmin && (
                      <>
                        <DropdownMenuItem onClick={() => navigate("/admin")} className="font-semibold text-red-600" data-testid="menu-admin">
                          <Shield className="w-4 h-4 mr-2" />
                          {t("nav.adminPanel")}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                      </>
                    )}
                    {isOrganizer && (
                      <>
                        <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">
                          <Home className="w-4 h-4 mr-2" />
                          {t("nav.dashboard")}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate("/my-events")} data-testid="menu-my-events">
                          <Calendar className="w-4 h-4 mr-2" />
                          {t("nav.myEvents")}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                      </>
                    )}
                    <DropdownMenuItem onClick={() => navigate("/favoritos")} data-testid="menu-favorites">
                      <Star className="w-4 h-4 mr-2" />
                      {t("nav.favorites")}
                      {!user.is_premium && <Lock className="w-3 h-3 ml-auto text-amber-500" />}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/my-registrations")} data-testid="menu-registrations">
                      <Users className="w-4 h-4 mr-2" />
                      {t("nav.myRegistrations")}
                    </DropdownMenuItem>
                    {!user.is_premium && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => navigate("/premium")} className="text-amber-600" data-testid="menu-premium">
                          <Crown className="w-4 h-4 mr-2" />
                          {t("nav.goPremium")}
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={logout} className="text-destructive" data-testid="logout-btn">
                      <LogOut className="w-4 h-4 mr-2" />
                      {t("nav.logout")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <Button onClick={() => login('/')} className="rounded-full" data-testid="login-btn">
                {t("nav.login")}
              </Button>
            )}

            {/* Mobile Menu */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden rounded-full" data-testid="mobile-menu">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent>
                <nav className="flex flex-col gap-2 mt-8">
                  {/* Mapa - Mobile */}
                  <Link
                    to="/mapa"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-muted transition-colors"
                  >
                    <MapPin className="w-5 h-5" />
                    <span className="md:hidden">{t("nav.map")}</span>
                  </Link>

                  {!user && (
                    <Link
                      to="/organizador"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-muted transition-colors"
                    >
                      <Sparkles className="w-5 h-5" />
                      {t("nav.beOrganizer")}
                    </Link>
                  )}
                  {user && isOrganizer && (
                    <>
                      <Link
                        to="/dashboard"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-muted transition-colors"
                      >
                        <Home className="w-5 h-5" />
                        {t("nav.dashboard")}
                      </Link>
                      <Button
                        onClick={() => {
                          navigate("/create-event");
                          setMobileMenuOpen(false);
                        }}
                        className="mt-4 rounded-full"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        {t("nav.createEvent")}
                      </Button>
                    </>
                  )}
                  {user && !isOrganizer && (
                    <Button
                      onClick={() => {
                        becomeOrganizer();
                        setMobileMenuOpen(false);
                      }}
                      variant="outline"
                      className="mt-4 rounded-full"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      {t("nav.beOrganizer")}
                    </Button>
                  )}
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

// ==================== EVENT CARD ====================

const EventCard = ({ event, index = 0, viewMode = "grid", grouped = false, onEdit }) => {
  const navigate = useNavigate();
  const CategoryIcon = getCategoryIcon(event.category);

  // Ubicación: nombre del sitio + población (si no está ya incluida en el nombre)
  const town = getEventTown(event);
  const showTown = town && !(event.location_name || "").toLowerCase().includes(town.toLowerCase());
  const locationLabel = [event.location_name, showTown ? town : null].filter(Boolean).join(" · ");

  const eventDate = new Date(event.start_date);
  const formattedDate = format(eventDate, "d MMM yyyy", { locale: es });
  const formattedTime = format(eventDate, "HH:mm");
  const formattedDay = format(eventDate, "d");
  const formattedMonth = format(eventDate, "MMM", { locale: es });

  if (viewMode === "list") {
    return (
      <Card 
        className="group overflow-hidden hover:shadow-lg transition-all duration-200 cursor-pointer animate-fadeIn"
        style={{ animationDelay: `${index * 0.03}s` }}
        onClick={() => navigate(`/evento/${event.event_id}`)}
        data-testid={`event-card-${event.event_id}`}
      >
        <div className="flex">
          {/* Date Badge: agrupado = solo hora con texto de color; si no, bloque de color con día/mes */}
          <div className={`w-20 flex-shrink-0 flex flex-col items-center justify-center ${grouped ? 'bg-transparent' : `${categoryColors[event.category] || 'bg-primary'} text-white`}`}>
            {grouped ? (
              <span className={`text-xl font-bold ${categoryTextColors[event.category] || 'text-primary'}`}>{formattedTime}</span>
            ) : (
              <>
                <span className="text-2xl font-bold">{formattedDay}</span>
                <span className="text-sm uppercase">{formattedMonth}</span>
              </>
            )}
          </div>
          
          {/* Content */}
          <div className="flex-1 p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="secondary" className="text-xs capitalize">{getCategoryName(event.category)}</Badge>
                </div>
                <h3 className="font-semibold text-lg mb-1 line-clamp-1 group-hover:text-primary transition-colors">
                  {event.title}
                </h3>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                  {!grouped && (
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {formattedTime}
                    </div>
                  )}
                  {locationLabel && (
                    <div className="flex items-center gap-1">
                      <MapPin className="w-4 h-4 flex-shrink-0" />
                      <span className="line-clamp-1">{locationLabel}</span>
                    </div>
                  )}
                </div>
              </div>
              {onEdit ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-shrink-0"
                  onClick={(e) => { e.stopPropagation(); onEdit(event); }}
                  data-testid={`edit-event-${event.event_id}`}
                >
                  <Edit className="w-4 h-4 mr-1" /> Editar
                </Button>
              ) : (
                <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
              )}
            </div>
          </div>
        </div>
      </Card>
    );
  }

  // Grid view (default)
  return (
    <Card 
      className="group overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-200 cursor-pointer animate-fadeIn"
      style={{ animationDelay: `${index * 0.05}s` }}
      onClick={() => navigate(`/evento/${event.event_id}`)}
      data-testid={`event-card-${event.event_id}`}
    >
      <div className="aspect-video relative overflow-hidden bg-muted">
        <img
          src={getEventImage(event)}
          alt={event.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <Badge className="absolute top-3 left-3 capitalize">{getCategoryName(event.category)}</Badge>
      </div>
      <CardContent className="p-4">
        <h3 className="font-semibold text-lg mb-2 line-clamp-1 group-hover:text-primary transition-colors">
          {event.title}
        </h3>
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            {formattedDate}
          </div>
          <div className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            {formattedTime}
          </div>
        </div>
        {locationLabel && (
          <div className="flex items-center gap-1 text-sm text-muted-foreground mt-2">
            <MapPin className="w-4 h-4 flex-shrink-0" />
            <span className="line-clamp-1">{locationLabel}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ==================== FOOTER ====================

const Footer = () => {
  const t = useT();
  return (
    <footer className="mt-16 py-12 border-t bg-muted/30">
      <div className="container mx-auto px-4 md:px-8">
        <div className="text-center text-muted-foreground">
          <p className="text-sm">&copy; 2026 Pobleo. {t("footer.rights")}</p>
        </div>
      </div>
    </footer>
  );
};

// ==================== MAPA: PANEL FLOTANTE DE EVENTOS EN PANTALLA ====================

// Observa el viewport del mapa y reporta los eventos dentro del rango visible.
const MapViewportTracker = ({ events, onChange }) => {
  const map = require('react-leaflet').useMap();
  const eventsRef = useRef(events);
  eventsRef.current = events;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const eventsKey = events.map((e) => e.event_id).join(",");

  useEffect(() => {
    if (!map) return;
    const update = () => {
      const bounds = map.getBounds();
      onChangeRef.current(
        eventsRef.current.filter((e) => e.coordinates && bounds.contains(e.coordinates))
      );
    };
    update();
    map.on("moveend", update);
    map.on("zoomend", update);
    return () => {
      map.off("moveend", update);
      map.off("zoomend", update);
    };
  }, [map, eventsKey]);

  return null;
};

// Tarjeta flotante (esquina del mapa) con los eventos visibles; plegable y scrollable.
const MapViewportPanel = ({ events, onSelect }) => {
  const t = useT();
  const [collapsed, setCollapsed] = useState(false);
  return (
    <div className="absolute top-3 right-3 z-[1000] w-64 max-w-[calc(100%-1.5rem)]">
      <div className="bg-background/95 backdrop-blur rounded-lg shadow-lg border overflow-hidden">
        <button
          onClick={() => setCollapsed((c) => !c)}
          className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium hover:bg-muted/50"
        >
          <span className="flex items-center gap-1.5">
            <Navigation className="w-3.5 h-3.5 text-primary" />
            {t("map.onScreen", { n: events.length })}
          </span>
          {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
        </button>
        {!collapsed && (
          <div className="max-h-[40vh] sm:max-h-72 overflow-y-auto divide-y">
            {events.length === 0 ? (
              <p className="px-3 py-4 text-xs text-muted-foreground text-center">
                {t("map.moveHint")}
              </p>
            ) : (
              events.map((ev) => {
                const d = new Date(ev.start_date);
                return (
                  <button
                    key={ev.event_id}
                    onClick={() => onSelect(ev)}
                    className="w-full text-left px-3 py-2 hover:bg-muted/50 flex gap-2 items-start"
                  >
                    <div className={`w-1.5 self-stretch rounded-full flex-shrink-0 ${categoryColors[ev.category] || "bg-primary"}`} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium line-clamp-1">{ev.title}</p>
                      <p className="text-xs text-muted-foreground capitalize">
                        {format(d, "d MMM", { locale: es })} · {format(d, "HH:mm")}
                      </p>
                      {getEventTown(ev) && (
                        <p className="text-xs text-muted-foreground line-clamp-1">{getEventTown(ev)}</p>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== MAIN PUBLIC PAGE - EVENT LISTING ====================

// Persistencia de filtros + vista entre navegaciones (se mantiene al cambiar de pantalla y volver)
const EVENTS_VIEW_STORAGE_KEY = "pobleo_events_view";
const loadPersistedEventsView = () => {
  try {
    const raw = sessionStorage.getItem(EVENTS_VIEW_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

// Botón flotante "volver al principio": aparece al bajar y hace scroll suave arriba
const ScrollToTopButton = () => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  if (!visible) return null;
  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      className="fixed bottom-6 right-6 z-[1000] w-11 h-11 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center hover:bg-primary/90 transition-colors"
      aria-label="Volver al principio"
      data-testid="scroll-to-top"
    >
      <ChevronUp className="w-5 h-5" />
    </button>
  );
};

const PublicEventsPage = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { townSlug } = require("react-router-dom").useParams(); // presente en /eventos/:townSlug
  const t = useT();
  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wakingUp, setWakingUp] = useState(false); // backend de Render "frío" arrancando
  const [visibleMapEvents, setVisibleMapEvents] = useState([]); // eventos dentro del viewport del mapa
  const [viewMode, setViewMode] = useState(() => {
    // "grid", "list", "calendar" or "map". Mantiene la preferencia guardada; si no hay,
    // por defecto LISTA en móvil (más cómodo) y GRID en pantallas grandes.
    const persisted = loadPersistedEventsView()?.viewMode;
    if (persisted) return persisted;
    const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
    return isMobile ? "list" : "grid";
  });
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Default filter: from today to 10 days ahead
  const getDefaultFilters = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tenDaysLater = new Date(today);
    tenDaysLater.setDate(tenDaysLater.getDate() + 10);
    return {
      category: "all",
      poblacion: "all",
      search: "",
      startDate: today,
      endDate: tenDaysLater,
      startTime: "00:00",
      endTime: "23:59"
    };
  };

  const [filters, setFilters] = useState(() => {
    const p = loadPersistedEventsView();
    if (p?.filters) {
      return {
        category: p.filters.category ?? "all",
        poblacion: p.filters.poblacion ?? "all",
        search: p.filters.search ?? "",
        startDate: p.filters.startDate ? new Date(p.filters.startDate) : null,
        endDate: p.filters.endDate ? new Date(p.filters.endDate) : null,
        startTime: p.filters.startTime ?? "00:00",
        endTime: p.filters.endTime ?? "23:59",
      };
    }
    return getDefaultFilters();
  });

  // Guardar filtros + vista para mantenerlos al navegar y volver
  useEffect(() => {
    try {
      sessionStorage.setItem(EVENTS_VIEW_STORAGE_KEY, JSON.stringify({
        viewMode,
        filters: {
          category: filters.category,
          poblacion: filters.poblacion,
          search: filters.search,
          startDate: filters.startDate ? filters.startDate.toISOString() : null,
          endDate: filters.endDate ? filters.endDate.toISOString() : null,
          startTime: filters.startTime,
          endTime: filters.endTime,
        },
      }));
    } catch {
      /* almacenamiento no disponible: ignorar */
    }
  }, [filters, viewMode]);
  
  // Count active filters (excluding default date range)
  const activeFilterCount = [
    filters.category !== "all",
    filters.poblacion !== "all",
    filters.search !== ""
  ].filter(Boolean).length;

  useEffect(() => {
    fetchCategories();
    
    // Load Leaflet CSS for map view
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css';
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
    // Refetch al cambiar filtros y al entrar/salir del calendario (que usa rango amplio).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, viewMode === "calendar"]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(registerCategories(response.data));
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchEvents = async () => {
    setLoading(true);
    setWakingUp(false);

    const params = new URLSearchParams();
    if (filters.category && filters.category !== "all") params.append("category", filters.category);
    if (filters.search) params.append("search", filters.search);
    // Rango en hora LOCAL sin conversión a UTC (el backend compara fechas naive contra
    // las de los eventos, guardadas en local). Aplica la hora elegida (o día completo por defecto).
    const toLocalNaiveISO = (date, timeStr, isEnd) => {
      const d = new Date(date);
      const m = /^(\d{1,2}):(\d{2})$/.exec(timeStr || "");
      if (m) {
        d.setHours(Number(m[1]), Number(m[2]), isEnd ? 59 : 0, isEnd ? 999 : 0);
      } else {
        if (isEnd) d.setHours(23, 59, 59, 999); else d.setHours(0, 0, 0, 0);
      }
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    };
    if (viewMode === "calendar") {
      // En la vista calendario el filtro de fecha NO aplica: cargamos un rango amplio
      // (1 mes atrás → 12 meses adelante) para poder seleccionar cualquier día.
      const from = new Date(); from.setMonth(from.getMonth() - 1);
      const to = new Date(); to.setFullYear(to.getFullYear() + 1);
      params.append("start_date", toLocalNaiveISO(from, "00:00", false));
      params.append("end_date", toLocalNaiveISO(to, "23:59", true));
    } else {
      if (filters.startDate) params.append("start_date", toLocalNaiveISO(filters.startDate, filters.startTime, false));
      if (filters.endDate) params.append("end_date", toLocalNaiveISO(filters.endDate, filters.endTime, true));
    }

    // En el plan gratuito de Render el backend se "duerme" y tarda en arrancar.
    // Reintentamos mientras esté arrancando (502/503/504/timeout) mostrando aviso.
    const MAX_RETRIES = 15;          // ~15 reintentos
    const RETRY_DELAY = 4000;        // cada 4s -> cubre ~60s de cold start
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

    // Si la primera respuesta tarda, avisamos al usuario aunque no haya fallado aún.
    const slowTimer = setTimeout(() => setWakingUp(true), 5000);

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        const response = await axios.get(`${API}/events?${params.toString()}`, { timeout: 65000 });
        clearTimeout(slowTimer);
        setEvents(response.data);
        setWakingUp(false);
        setLoading(false);
        return;
      } catch (error) {
        const status = error?.response?.status;
        // El servidor existe pero está arrancando -> reintentar
        const isColdStart = status === 502 || status === 503 || status === 504 || error?.code === "ECONNABORTED";
        if (isColdStart && attempt < MAX_RETRIES) {
          setWakingUp(true);
          await sleep(RETRY_DELAY);
          continue;
        }
        // Sin servidor o se agotaron los reintentos
        clearTimeout(slowTimer);
        console.error("Error fetching events:", error);
        setEvents([]);
        setWakingUp(false);
        setLoading(false);
        return;
      }
    }
  };

  // Filtro de población: aplica a grid/lista/calendario. El mapa NO lo aplica
  // (allí ya se navega con el propio mapa), por eso usa `events` sin filtrar.
  const displayedEvents = useMemo(() => {
    // En página de pueblo (/eventos/:townSlug) se filtra por ese municipio.
    if (townSlug) {
      return events.filter((e) => { const t = getEventTown(e); return t && slugifyTown(t) === townSlug; });
    }
    return filters.poblacion && filters.poblacion !== "all"
      ? events.filter((e) => getEventTown(e) === filters.poblacion)
      : events;
  }, [events, filters.poblacion, townSlug]);

  // Poblaciones disponibles (derivadas de los eventos cargados) para el selector
  const poblaciones = useMemo(() => {
    const set = new Set();
    events.forEach((e) => { const t = getEventTown(e); if (t) set.add(t); });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "es"));
  }, [events]);

  // Nombre de la población de la página de pueblo (para SEO y título)
  const townName = useMemo(() => {
    if (!townSlug) return null;
    return poblaciones.find((p) => slugifyTown(p) === townSlug)
      || townSlug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  }, [townSlug, poblaciones]);

  const eventsForSelectedDate = displayedEvents.filter(event => {
    const eventDate = new Date(event.start_date);
    return eventDate.toDateString() === selectedDate.toDateString();
  });

  const eventDates = displayedEvents.map(e => new Date(e.start_date).toDateString());
  // Memoizado: evita recrear el array (y reconstruir los marcadores / re-encajar el mapa) en cada render
  const mappableEvents = useMemo(() => getEventsWithCoordinates(events), [events]);

  return (
    <div className="min-h-screen bg-background" data-testid="public-events-page">
      <Seo
        title={townName ? t("seo.town.title", { town: townName }) : t("seo.home.title")}
        description={townName ? t("seo.town.desc", { town: townName }) : t("seo.home.desc")}
        path={townSlug ? `/eventos/${townSlug}` : undefined}
      />
      <div className="container mx-auto px-4 md:px-8 py-4 md:py-8">
        {/* Header - Desktop */}
        <div className="hidden md:flex md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-1">
              {townName ? t("town.h1", { town: townName }) : t("page.discover")}
            </h1>
            <p className="text-muted-foreground text-sm">
              {townName ? (
                <>{t("town.sub", { town: townName })} · <Link to="/eventos" className="text-primary hover:underline">{t("town.seeAll")}</Link></>
              ) : t("page.discover.sub")}
            </p>
          </div>
          
          {/* View Toggle - Desktop */}
          <div className="flex items-center gap-2 bg-muted p-1 rounded-lg">
            <Button
              variant={viewMode === "grid" ? "default" : "ghost"}
              size="sm"
              onClick={() => setViewMode("grid")}
              className="rounded-md"
              data-testid="view-grid-btn"
            >
              <Grid3X3 className="w-4 h-4 lg:mr-2" />
              <span className="hidden lg:inline">{t("view.grid")}</span>
            </Button>
            <Button
              variant={viewMode === "list" ? "default" : "ghost"}
              size="sm"
              onClick={() => setViewMode("list")}
              className="rounded-md"
              data-testid="view-list-btn"
            >
              <List className="w-4 h-4 lg:mr-2" />
              <span className="hidden lg:inline">{t("view.list")}</span>
            </Button>
            <Button
              variant={viewMode === "calendar" ? "default" : "ghost"}
              size="sm"
              onClick={() => setViewMode("calendar")}
              className="rounded-md"
              data-testid="view-calendar-btn"
            >
              <CalendarRange className="w-4 h-4 lg:mr-2" />
              <span className="hidden lg:inline">{t("view.calendar")}</span>
            </Button>
            <Button
              variant={viewMode === "map" ? "default" : "ghost"}
              size="sm"
              onClick={() => setViewMode("map")}
              className="rounded-md"
              data-testid="view-mapa-btn"
            >
              <MapPin className="w-4 h-4 lg:mr-2" />
              <span className="hidden lg:inline">{t("nav.map")}</span>
            </Button>
          </div>
        </div>
        
        {/* Header - Mobile (compact) */}
        <div className="md:hidden mb-4">
          <h1 className="text-xl font-bold tracking-tight">
            {townName ? t("town.h1", { town: townName }) : t("page.discover")}
          </h1>
          {townName && (
            <Link to="/eventos" className="text-xs text-primary hover:underline">← {t("town.seeAll")}</Link>
          )}
        </div>

        {/* Filters - Desktop */}
        <Card className="mb-8 hidden md:block">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder={t("filter.searchEvents")}
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    className="pl-10"
                    data-testid="search-input"
                  />
                </div>
              </div>

              {/* Category */}
              <Select value={filters.category} onValueChange={(v) => setFilters({ ...filters, category: v })}>
                <SelectTrigger className="w-full md:w-48" data-testid="category-filter">
                  <SelectValue placeholder={t("filter.category")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("filter.allCategories")}</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Población (no aplica en la vista mapa; oculto en páginas de pueblo) */}
              {!townSlug && (
              <Select value={filters.poblacion} onValueChange={(v) => setFilters({ ...filters, poblacion: v })}>
                <SelectTrigger className="w-full md:w-48" data-testid="poblacion-filter">
                  <SelectValue placeholder={t("filter.town")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("filter.allTowns")}</SelectItem>
                  {poblaciones.map((p) => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              )}

              {/* Date Range (no aplica en la vista calendario) */}
              {viewMode !== "calendar" && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full md:w-auto md:min-w-[12rem] justify-start" data-testid="date-filter">
                    <Calendar className="w-4 h-4 mr-2 flex-shrink-0" />
                    <span className="truncate">
                      {filters.startDate ? format(filters.startDate, "d MMM", { locale: es }) : t("filter.date")}
                      {filters.startTime && filters.startTime !== "00:00" ? ` ${filters.startTime}` : ""}
                      {filters.endDate ? ` - ${format(filters.endDate, "d MMM", { locale: es })}` : ""}
                      {filters.endTime && filters.endTime !== "23:59" ? ` ${filters.endTime}` : ""}
                    </span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarPicker
                    mode="range"
                    defaultMonth={filters.startDate || filters.endDate || new Date()}
                    selected={{ from: filters.startDate, to: filters.endDate }}
                    onSelect={(range) => setFilters({ ...filters, startDate: range?.from, endDate: range?.to })}
                    locale={es}
                  />
                  <div className="flex items-end gap-2 p-3 border-t">
                    <div className="flex-1">
                      <label className="text-xs text-muted-foreground mb-1 block">Desde (hora)</label>
                      <Input type="time" value={filters.startTime || "00:00"} onChange={(e) => setFilters({ ...filters, startTime: e.target.value })} className="h-9" />
                    </div>
                    <div className="flex-1">
                      <label className="text-xs text-muted-foreground mb-1 block">Hasta (hora)</label>
                      <Input type="time" value={filters.endTime || "23:59"} onChange={(e) => setFilters({ ...filters, endTime: e.target.value })} className="h-9" />
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
              )}

              {/* Clear Filters - shows when filters differ from default */}
              {(filters.category !== "all" || filters.poblacion !== "all" || filters.search) && (
                <Button
                  variant="ghost"
                  onClick={() => setFilters(getDefaultFilters())}
                  data-testid="clear-filters"
                >
                  <X className="w-4 h-4 mr-2" />
                  {t("filter.clear")}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Filters - Mobile */}
        <div className="mb-4 md:hidden">
          <div className="flex gap-2">
            {/* Search - always visible on mobile */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={t("filter.search")}
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-10 h-9"
                data-testid="search-input-mobile"
              />
            </div>
            
            {/* View Toggle Button - Mobile */}
            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9"
              onClick={() => {
                if (viewMode === "grid") setViewMode("list");
                else if (viewMode === "list") setViewMode("calendar");
                else if (viewMode === "calendar") setViewMode("map");
                else setViewMode("grid");
              }}
              data-testid="mobile-view-toggle"
            >
              {viewMode === "grid" && <List className="w-4 h-4" />}
              {viewMode === "list" && <CalendarRange className="w-4 h-4" />}
              {viewMode === "calendar" && <MapPin className="w-4 h-4" />}
              {viewMode === "map" && <Grid3X3 className="w-4 h-4" />}
            </Button>
            
            {/* Filter Button - opens sheet */}
            <Sheet open={showMobileFilters} onOpenChange={setShowMobileFilters}>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon" className="relative h-9 w-9" data-testid="mobile-filter-btn">
                  <Filter className="w-4 h-4" />
                  {activeFilterCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-primary text-primary-foreground text-[10px] rounded-full flex items-center justify-center">
                      {activeFilterCount}
                    </span>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent side="bottom" className="h-auto max-h-[70vh]">
                <div className="pt-3 pb-5 space-y-5">
                  <div className="flex items-center justify-between">
                    <h3 className="text-base font-semibold">{t("filter.title")}</h3>
                    {(filters.category !== "all" || filters.poblacion !== "all" || filters.search) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setFilters(getDefaultFilters());
                          setShowMobileFilters(false);
                        }}
                      >
                        <X className="w-4 h-4 mr-1" />
                        {t("filter.clear")}
                      </Button>
                    )}
                  </div>

                  {/* Category */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">{t("filter.category")}</Label>
                    <Select value={filters.category} onValueChange={(v) => setFilters({ ...filters, category: v })}>
                      <SelectTrigger data-testid="category-filter-mobile">
                        <SelectValue placeholder={t("filter.allCategories")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t("filter.allCategories")}</SelectItem>
                        {categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Población (oculto en páginas de pueblo) */}
                  {!townSlug && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">{t("filter.town")}</Label>
                    <Select value={filters.poblacion} onValueChange={(v) => setFilters({ ...filters, poblacion: v })}>
                      <SelectTrigger data-testid="poblacion-filter-mobile">
                        <SelectValue placeholder={t("filter.allTowns")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t("filter.allTowns")}</SelectItem>
                        {poblaciones.map((p) => (
                          <SelectItem key={p} value={p}>{p}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  )}

                  {/* Date Range (no aplica en la vista calendario) */}
                  {viewMode !== "calendar" && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">{t("filter.dateRange")}</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start" data-testid="date-filter-mobile">
                          <Calendar className="w-4 h-4 mr-2 flex-shrink-0" />
                          <span className="truncate">
                            {filters.startDate ? format(filters.startDate, "d MMM", { locale: es }) : t("filter.dateStart")}
                            {filters.startTime && filters.startTime !== "00:00" ? ` ${filters.startTime}` : ""}
                            {filters.endDate ? ` - ${format(filters.endDate, "d MMM", { locale: es })}` : ""}
                            {filters.endTime && filters.endTime !== "23:59" ? ` ${filters.endTime}` : ""}
                          </span>
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="center">
                        <CalendarPicker
                          mode="range"
                          defaultMonth={filters.startDate || filters.endDate || new Date()}
                          selected={{ from: filters.startDate, to: filters.endDate }}
                          onSelect={(range) => setFilters({ ...filters, startDate: range?.from, endDate: range?.to })}
                          locale={es}
                        />
                        <div className="flex items-end gap-2 p-3 border-t">
                          <div className="flex-1">
                            <label className="text-xs text-muted-foreground mb-1 block">Desde (hora)</label>
                            <Input type="time" value={filters.startTime || "00:00"} onChange={(e) => setFilters({ ...filters, startTime: e.target.value })} className="h-9" />
                          </div>
                          <div className="flex-1">
                            <label className="text-xs text-muted-foreground mb-1 block">Hasta (hora)</label>
                            <Input type="time" value={filters.endTime || "23:59"} onChange={(e) => setFilters({ ...filters, endTime: e.target.value })} className="h-9" />
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                  )}

                  {/* Apply Button */}
                  <Button 
                    className="w-full" 
                    onClick={() => setShowMobileFilters(false)}
                    data-testid="apply-filters-mobile"
                  >
                    {t("filter.see")} {displayedEvents.length} {t("word.events")}
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
          
          {/* Active Filters Pills */}
          {(filters.category !== "all" || filters.poblacion !== "all" || (filters.startDate && filters.endDate)) && (
            <div className="flex flex-wrap gap-2 mt-3">
              {filters.category !== "all" && (
                <Badge variant="secondary" className="pl-2 pr-1 py-1 flex items-center gap-1">
                  {getCategoryName(filters.category)}
                  <button
                    onClick={() => setFilters({ ...filters, category: "all" })}
                    className="ml-1 hover:bg-muted rounded-full p-0.5"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              )}
              {filters.poblacion !== "all" && (
                <Badge variant="secondary" className="pl-2 pr-1 py-1 flex items-center gap-1">
                  {filters.poblacion}
                  <button
                    onClick={() => setFilters({ ...filters, poblacion: "all" })}
                    className="ml-1 hover:bg-muted rounded-full p-0.5"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              )}
              {filters.startDate && filters.endDate && (
                <Badge variant="secondary" className="pl-2 pr-1 py-1 flex items-center gap-1">
                  {format(filters.startDate, "d MMM", { locale: es })} - {format(filters.endDate, "d MMM", { locale: es })}
                </Badge>
              )}
            </div>
          )}
        </div>

        {/* Aviso: backend "frío" de Render arrancando */}
        {wakingUp && (
          <div className="mb-6 flex items-center gap-3 rounded-lg border border-primary/30 bg-primary/10 px-4 py-3 text-sm">
            <Loader2 className="w-5 h-5 animate-spin text-primary flex-shrink-0" />
            <div>
              <p className="font-medium text-foreground">{t("loading.events")}</p>
              <p className="text-muted-foreground">El servidor estaba en reposo y está arrancando. Esto puede tardar unos segundos la primera vez.</p>
            </div>
          </div>
        )}

        {/* Enlaces a páginas de pueblo (SEO interno + navegación). Solo fuera de una página de pueblo. */}
        {!townSlug && poblaciones.length > 0 && (
          <div className="mb-6 flex flex-wrap items-center gap-2">
            <span className="text-sm text-muted-foreground mr-1">{t("town.byTown")}</span>
            {poblaciones.map((p) => (
              <Link
                key={p}
                to={`/eventos/${slugifyTown(p)}`}
                className="text-xs px-3 py-1 rounded-full border hover:bg-muted transition-colors"
              >
                {p}
              </Link>
            ))}
          </div>
        )}

        {/* Content based on view mode */}
        {viewMode === "grid" ? (
          // Grid View
          <>
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {[...Array(8)].map((_, i) => (
                  <Card key={i} className="animate-pulse overflow-hidden">
                    <div className="aspect-video bg-muted" />
                    <CardContent className="p-4">
                      <div className="h-4 bg-muted rounded w-3/4 mb-2" />
                      <div className="h-6 bg-muted rounded w-full mb-2" />
                      <div className="h-4 bg-muted rounded w-1/2" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : displayedEvents.length === 0 ? (
              <Card className="p-12 text-center">
                <CalendarDays className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold mb-2">{t("empty.title")}</h3>
                <p className="text-muted-foreground mb-4">{t("empty.sub")}</p>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {displayedEvents.map((event, i) => (
                  <EventCard key={event.event_id} event={event} index={i} viewMode="grid" />
                ))}
              </div>
            )}
          </>
        ) : viewMode === "list" ? (
          // List View
          <>
            {loading ? (
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <Card key={i} className="animate-pulse">
                    <div className="flex">
                      <div className="w-20 h-24 bg-muted" />
                      <div className="flex-1 p-4">
                        <div className="h-4 bg-muted rounded w-20 mb-2" />
                        <div className="h-6 bg-muted rounded w-3/4 mb-2" />
                        <div className="h-4 bg-muted rounded w-1/2" />
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : displayedEvents.length === 0 ? (
              <Card className="p-12 text-center">
                <CalendarDays className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold mb-2">{t("empty.title")}</h3>
                <p className="text-muted-foreground mb-4">{t("empty.sub")}</p>
              </Card>
            ) : (
              <div className="space-y-8">
                {Object.entries(
                  displayedEvents.reduce((groups, event) => {
                    const dayKey = format(new Date(event.start_date), "yyyy-MM-dd");
                    (groups[dayKey] = groups[dayKey] || []).push(event);
                    return groups;
                  }, {})
                )
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([dayKey, dayEvents]) => (
                    <div key={dayKey} className="relative pl-7">
                      {/* Línea vertical del timeline (un segmento por día) */}
                      <div className="absolute left-[7px] top-3 bottom-2 w-0.5 bg-border" aria-hidden="true" />
                      {/* Cabecera de día con fondo de color y dot grande sobre la línea */}
                      <div className="relative flex items-center gap-3 mb-3 rounded-lg bg-gradient-to-r from-primary/20 via-primary/10 to-transparent px-3 py-2">
                        <span className="absolute -left-7 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-primary ring-4 ring-background" aria-hidden="true" />
                        <h3 className="text-base md:text-lg font-bold capitalize text-foreground">
                          {format(new Date(`${dayKey}T00:00:00`), "EEEE, d 'de' MMMM yyyy", { locale: es })}
                        </h3>
                        <Badge variant="secondary" className="rounded-full bg-background">
                          {dayEvents.length} {dayEvents.length === 1 ? "evento" : "eventos"}
                        </Badge>
                      </div>
                      {/* Eventos del día, ordenados por hora */}
                      <div className="space-y-3">
                        {[...dayEvents]
                          .sort((x, y) => new Date(x.start_date) - new Date(y.start_date))
                          .map((event, i) => (
                            <div key={event.event_id} className="relative">
                              {/* Dot del evento (color por categoría) sobre la línea */}
                              <span className={`absolute -left-[26px] top-1/2 -translate-y-1/2 w-3 h-3 rounded-full ring-4 ring-background ${categoryColors[event.category] || 'bg-primary'}`} aria-hidden="true" />
                              <EventCard event={event} index={i} viewMode="list" grouped />
                            </div>
                          ))}
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </>
        ) : viewMode === "calendar" ? (
          // Calendar View
          <div className="grid lg:grid-cols-5 gap-4 md:gap-8">
            {/* Calendar */}
            <Card className="lg:col-span-3">
              <CardContent className="p-2 sm:p-4 md:p-6 lg:p-8">
                <CalendarPicker
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => date && setSelectedDate(date)}
                  locale={es}
                  className="rdp-compact md:rdp-large w-full mx-auto"
                  modifiers={{
                    hasEvent: (date) => eventDates.includes(date.toDateString())
                  }}
                  modifiersStyles={{
                    hasEvent: {
                      fontWeight: 'bold',
                      backgroundColor: 'hsl(var(--primary) / 0.15)',
                      borderRadius: '50%'
                    }
                  }}
                  classNames={{
                    months: "flex flex-col sm:flex-row space-y-2 sm:space-x-4 sm:space-y-0 w-full",
                    month: "space-y-2 md:space-y-4 w-full",
                    caption: "flex justify-center pt-1 relative items-center mb-2 md:mb-4",
                    caption_label: "text-base md:text-xl lg:text-2xl font-semibold",
                    nav: "space-x-1 flex items-center",
                    nav_button: "h-7 w-7 md:h-10 md:w-10 bg-transparent p-0 opacity-50 hover:opacity-100",
                    table: "w-full border-collapse",
                    head_row: "flex w-full",
                    head_cell: "text-muted-foreground rounded-md flex-1 font-medium text-xs md:text-sm py-1 md:py-2",
                    row: "flex w-full mt-0.5 md:mt-1",
                    cell: "flex-1 text-center text-xs md:text-sm p-0 relative focus-within:relative focus-within:z-20",
                    day: "h-8 w-8 md:h-12 md:w-12 lg:h-14 lg:w-14 p-0 font-normal aria-selected:opacity-100 hover:bg-accent hover:text-accent-foreground rounded-full mx-auto flex items-center justify-center text-sm md:text-base",
                    day_selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
                    day_today: "bg-accent text-accent-foreground",
                    day_outside: "text-muted-foreground opacity-50",
                    day_disabled: "text-muted-foreground opacity-50",
                    day_hidden: "invisible",
                  }}
                />
              </CardContent>
            </Card>

            {/* Events for selected date */}
            <div className="lg:col-span-2">
              <h2 className="text-base md:text-xl font-semibold mb-3 md:mb-4">
                {format(selectedDate, "EEE, d 'de' MMM", { locale: es })}
              </h2>
              {loading ? (
                <div className="space-y-3">
                  {[...Array(3)].map((_, i) => (
                    <Card key={i} className="animate-pulse p-4">
                      <div className="h-5 bg-muted rounded w-3/4 mb-2" />
                      <div className="h-4 bg-muted rounded w-1/2" />
                    </Card>
                  ))}
                </div>
              ) : eventsForSelectedDate.length === 0 ? (
                <Card className="p-6 text-center">
                  <Calendar className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                  <p className="text-muted-foreground">No hay eventos para esta fecha</p>
                </Card>
              ) : (
                <ScrollArea className="h-[500px]">
                  <div className="space-y-3 pr-4">
                    {eventsForSelectedDate.map((event, i) => (
                      <EventCard key={event.event_id} event={event} index={i} viewMode="list" />
                    ))}
                  </div>
                </ScrollArea>
              )}
            </div>
          </div>
        ) : viewMode === "map" ? (
          // Map View
          <Card>
            <CardContent className="p-4">
              <div className="h-[600px] rounded-lg overflow-hidden bg-muted relative isolate">
                {mappableEvents.length > 0 ? (
                  <>
                  <MapContainer center={mappableEvents[0].coordinates} zoom={11} maxZoom={20} style={{ height: "100%", width: "100%" }}>
                    <TileLayer
                      url={getMapTileUrl(theme)}
                      attribution={MAP_TILES_ATTR}
                      maxZoom={20}
                    />
                    <MapAutoFit events={mappableEvents} padding={[72, 72]} singlePointZoom={16} maxZoom={16} />
                    <MapViewportTracker events={mappableEvents} onChange={setVisibleMapEvents} />
                    <MarkerClusterGroup
                      chunkedLoading
                      showCoverageOnHover={false}
                      maxClusterRadius={50}
                    >
                      {mappableEvents.map((event) => {
                        const PopupCategoryIcon = getCategoryIcon(event.category);
                        const popupDate = new Date(event.start_date);
                        return (
                        <Marker
                          key={event.event_id}
                          position={event.coordinates}
                        >
                          <Popup className="custom-popup" minWidth={250} maxWidth={300}>
                            <div style={{ margin: '-1px -1px 0 -1px' }}>
                              {/* Cabecera con color de categoría */}
                              <div
                                className={`px-3 py-2 ${categoryColors[event.category] || 'bg-primary'}`}
                                style={{ borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}
                              >
                                <div className="flex items-center gap-2 text-white">
                                  <PopupCategoryIcon className="w-4 h-4" />
                                  <span className="text-xs font-medium uppercase tracking-wide">
                                    {getCategoryName(event.category)}
                                  </span>
                                </div>
                              </div>
                              {/* Contenido */}
                              <div className="p-[10px]">
                                <h3 className="font-bold text-base mb-2 text-gray-900 leading-tight line-clamp-2">
                                  {event.title}
                                </h3>
                                <div className="space-y-1.5 text-sm">
                                  <div className="flex items-center gap-2 text-gray-600">
                                    <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
                                    <span className="capitalize">{format(popupDate, "EEE d MMM", { locale: es })}</span>
                                    <Clock className="w-3.5 h-3.5 flex-shrink-0 ml-1" />
                                    <span>{format(popupDate, "HH:mm")}</span>
                                  </div>
                                  {event.location_name && (
                                    <div className="flex items-center gap-2 text-gray-600">
                                      <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                                      <span className="line-clamp-1">{event.location_name}</span>
                                    </div>
                                  )}
                                </div>
                                <button
                                  onClick={() => navigate(`/evento/${event.event_id}`)}
                                  className="mt-3 w-full py-2 px-3 bg-gradient-to-r from-sky-500 to-blue-600 text-white text-sm font-medium rounded-lg hover:from-sky-600 hover:to-blue-700 transition-colors flex items-center justify-center gap-2"
                                >
                                  Ver detalles
                                  <ChevronRight className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </Popup>
                        </Marker>
                        );
                      })}
                    </MarkerClusterGroup>
                  </MapContainer>
                  {/* Panel flotante solo en tablet/escritorio (en móvil tapaba el mapa) */}
                  <div className="hidden md:block">
                    <MapViewportPanel events={visibleMapEvents} onSelect={(ev) => navigate(`/evento/${ev.event_id}`)} />
                  </div>
                  </>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-muted-foreground">{t("map.noLocation")}</p>
                  </div>
                )}
              </div>

              {/* En móvil: eventos visibles debajo del mapa (el panel flotante se oculta) */}
              <div className="md:hidden mt-4">
                <p className="text-sm font-medium mb-2 flex items-center gap-1.5">
                  <Navigation className="w-3.5 h-3.5 text-primary" />
                  {t("map.onScreen", { n: visibleMapEvents.length })}
                </p>
                {visibleMapEvents.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-3">{t("map.moveHint")}</p>
                ) : (
                  <div className="divide-y rounded-lg border max-h-[50vh] overflow-y-auto">
                    {visibleMapEvents.map((ev) => {
                      const d = new Date(ev.start_date);
                      return (
                        <button
                          key={ev.event_id}
                          onClick={() => navigate(`/evento/${ev.event_id}`)}
                          className="w-full text-left px-3 py-2 hover:bg-muted/50 flex gap-2 items-start"
                        >
                          <div className={`w-1.5 self-stretch rounded-full flex-shrink-0 ${categoryColors[ev.category] || "bg-primary"}`} />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium line-clamp-1">{ev.title}</p>
                            <p className="text-xs text-muted-foreground capitalize">
                              {format(d, "d MMM", { locale: es })} · {format(d, "HH:mm")}
                            </p>
                            {getEventTown(ev) && (
                              <p className="text-xs text-muted-foreground line-clamp-1">{getEventTown(ev)}</p>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <p className="text-xs text-muted-foreground mt-4">
                {t("map.withLocation", { n: mappableEvents.length })}
              </p>
            </CardContent>
          </Card>
        ) : null}
      </div>
      {(viewMode === "grid" || viewMode === "list") && <ScrollToTopButton />}
      <Footer />
    </div>
  );
};

// ==================== ORGANIZER LANDING PAGE ====================

const OrganizerLandingPage = () => {
  const { user, login } = useAuth();
  const t = useT();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  return (
    <div className="min-h-screen" data-testid="organizer-landing">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 md:py-32">
        <div className="absolute inset-0 bg-gradient-to-br from-sky-100 via-orange-50 to-blue-100 dark:from-sky-950 dark:via-slate-900 dark:to-blue-950" />
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{ backgroundImage: `url(https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1920&q=80)` }}
        />
        <div className="container mx-auto px-4 md:px-8 relative">
          <div className="max-w-3xl">
            <Badge className="mb-4 bg-gradient-to-r from-orange-400 to-orange-600 text-white border-0">
              <Sparkles className="w-3 h-3 mr-1" />
              {t("org.badge")}
            </Badge>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-none mb-6 animate-fadeIn">
              {t("org.heroTitle1")}<br />
              <span className="bg-gradient-to-r from-sky-500 to-orange-500 bg-clip-text text-transparent">{t("org.heroTitle2")}</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-8 leading-relaxed max-w-2xl animate-fadeIn" style={{ animationDelay: '0.1s' }}>
              {t("org.heroSub")}
            </p>
            <div className="flex flex-wrap gap-4 animate-fadeIn" style={{ animationDelay: '0.2s' }}>
              <Button onClick={login} size="lg" className="rounded-full px-8 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 shadow-lg" data-testid="hero-cta">
                {t("org.ctaFree")}
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
              <Button variant="outline" size="lg" className="rounded-full px-8 border-2" onClick={() => navigate("/")} data-testid="explore-btn">
                {t("org.seeEvents")}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gradient-to-b from-white to-sky-50 dark:from-background dark:to-sky-950/20">
        <div className="container mx-auto px-4 md:px-8">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-12 text-center">
            {t("org.featuresH2")}
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Calendar, title: t("org.f1t"), desc: t("org.f1d"), color: "from-sky-400 to-blue-600" },
              { icon: Users, title: t("org.f2t"), desc: t("org.f2d"), color: "from-orange-400 to-orange-600" },
              { icon: Bell, title: t("org.f3t"), desc: t("org.f3d"), color: "from-amber-400 to-orange-500" },
              { icon: MapPin, title: t("org.f4t"), desc: t("org.f4d"), color: "from-emerald-400 to-teal-600" },
              { icon: LayoutGrid, title: t("org.f5t"), desc: t("org.f5d"), color: "from-cyan-400 to-sky-600" },
              { icon: Eye, title: t("org.f6t"), desc: t("org.f6d"), color: "from-blue-400 to-indigo-600" },
            ].map((feature, i) => (
              <Card key={i} className="group hover:shadow-xl hover:-translate-y-1 transition-all duration-200 border-0 shadow-md" data-testid={`feature-${i}`}>
                <CardHeader>
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-4 shadow-md`}>
                    <feature.icon className="w-6 h-6 text-white" />
                  </div>
                  <CardTitle className="text-xl">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">{feature.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20">
        <div className="container mx-auto px-4 md:px-8">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-12 text-center">
            {t("org.howH2")}
          </h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              { step: "1", title: t("org.s1t"), desc: t("org.s1d"), color: "from-sky-400 to-blue-600" },
              { step: "2", title: t("org.s2t"), desc: t("org.s2d"), color: "from-orange-400 to-orange-600" },
              { step: "3", title: t("org.s3t"), desc: t("org.s3d"), color: "from-emerald-400 to-teal-600" },
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${item.color} text-white text-2xl font-bold flex items-center justify-center mx-auto mb-4 shadow-lg`}>
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                <p className="text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-sky-500 via-blue-600 to-sky-600 text-white">
        <div className="container mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-6">
            {t("org.ctaH2")}
          </h2>
          <p className="text-lg opacity-90 mb-8 max-w-xl mx-auto">
            {t("org.ctaSub")}
          </p>
          <Button onClick={login} size="lg" className="rounded-full px-8 bg-white text-sky-600 hover:bg-gray-100 shadow-lg" data-testid="cta-btn">
            {t("org.ctaBtn")}
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t">
        <div className="container mx-auto px-4 md:px-8 text-center text-muted-foreground">
          <p>&copy; 2026 Pobleo. {t("footer.rights")}</p>
        </div>
      </footer>
    </div>
  );
};

// ==================== MAP AUTO-FIT COMPONENT ====================

const getEventCoordinates = (event) => {
  const latitude = Number.parseFloat(event.latitude);
  const longitude = Number.parseFloat(event.longitude);

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return null;
  }

  return [latitude, longitude];
};

const getEventsWithCoordinates = (events) => events
  .map((event) => {
    const coordinates = getEventCoordinates(event);
    return coordinates ? { ...event, coordinates } : null;
  })
  .filter(Boolean);

const MapAutoFit = ({ events, padding = [72, 72], singlePointZoom = 16, maxZoom = 16 }) => {
  const map = require('react-leaflet').useMap();
  const L = require('leaflet');
  // Clave estable: solo re-encaja cuando cambia el CONJUNTO de eventos,
  // no en cada render (si no, al mover el mapa volvía siempre al punto inicial).
  const eventsKey = events.map((e) => e.event_id).join(",");
  const lastFittedKey = useRef(null);

  useEffect(() => {
    if (events.length === 0) {
      return;
    }
    // Si ya encajamos para este mismo conjunto, no volver a mover el mapa
    if (lastFittedKey.current === eventsKey) {
      return;
    }
    lastFittedKey.current = eventsKey;

    const coordinates = events
      .map((event) => event.coordinates || getEventCoordinates(event))
      .filter(Boolean);

    if (coordinates.length === 0) {
      return;
    }

    if (coordinates.length === 1) {
      map.setView(coordinates[0], singlePointZoom, { animate: false });
      return;
    }

    const bounds = L.latLngBounds(coordinates);

    map.fitBounds(bounds, {
      padding,
      maxZoom,
      animate: false
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventsKey, map]);

  return null;
};

const MapSelectionController = ({ selectedEvent, markerRefs, focusZoom = 15 }) => {
  const map = require('react-leaflet').useMap();

  useEffect(() => {
    if (!selectedEvent) {
      return;
    }

    Object.entries(markerRefs.current).forEach(([eventId, marker]) => {
      if (eventId !== String(selectedEvent.event_id) && marker) {
        marker.closePopup();
      }
    });

    map.flyTo(selectedEvent.coordinates, focusZoom, {
      animate: true,
      duration: 0.6
    });

    const marker = markerRefs.current[selectedEvent.event_id];
    if (marker) {
      map.once('moveend', () => {
        marker.openPopup();
      });
    }
  }, [focusZoom, map, markerRefs, selectedEvent]);

  return null;
};

// ==================== PUBLIC MAP PAGE ====================

const PublicMapPage = () => {
  const { theme } = useTheme();
  const t = useT();
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [visibleMapEvents, setVisibleMapEvents] = useState([]); // eventos dentro del viewport
  const navigate = useNavigate();
  // Memoizado: evita recrear el array (y reconstruir marcadores / re-encajar) en cada render
  const mappableEvents = useMemo(() => getEventsWithCoordinates(events), [events]);
  const markerRefs = useRef({});
  const cardRefs = useRef({});

  useEffect(() => {
    fetchEvents();
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(link);
    setMapLoaded(true);
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`);
      setEvents(getEventsWithCoordinates(response.data));
    } catch (error) {
      console.error("Error fetching events:", error);
    }
  };

  const MapContainer = mapLoaded ? require('react-leaflet').MapContainer : null;
  const TileLayer = mapLoaded ? require('react-leaflet').TileLayer : null;
  const Marker = mapLoaded ? require('react-leaflet').Marker : null;
  const Popup = mapLoaded ? require('react-leaflet').Popup : null;
  const L = mapLoaded ? require('leaflet') : null;

  const getMarkerIcon = (isSelected) => {
    if (!mapLoaded) {
      return null;
    }

    return new L.DivIcon({
      className: isSelected ? 'custom-marker custom-marker-selected' : 'custom-marker',
      iconSize: isSelected ? [30, 30] : [24, 24],
      iconAnchor: isSelected ? [15, 15] : [12, 12]
    });
  };

  const formatEventDate = (dateStr) => {
    const date = new Date(dateStr);
    return format(date, "d MMM yyyy, HH:mm", { locale: es });
  };

  const focusEventOnMap = (event) => {
    setSelectedEvent(event);
  };

  useEffect(() => {
    if (!selectedEvent) {
      return;
    }

    const card = cardRefs.current[selectedEvent.event_id];
    if (card) {
      card.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }
  }, [selectedEvent]);

  return (
    <div className="min-h-screen bg-background" data-testid="map-page">
      <Seo
        title={t("seo.mapa.title")}
        description={t("seo.mapa.desc")}
        path="/mapa"
      />
      <div className="container mx-auto px-4 md:px-8 py-8">
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-8">{t("page.mapH1")}</h1>

        <div className="grid lg:grid-cols-3 gap-8">
          <Card className="lg:col-span-2 overflow-hidden">
            <div className="aspect-video lg:aspect-auto lg:h-[600px] relative isolate">
              {mapLoaded && MapContainer && (
                <>
                <MapContainer
                  center={[40.4168, -3.7038]}
                  zoom={6}
                  maxZoom={20}
                  style={{ height: '100%', width: '100%' }}
                  data-testid="map-container"
                >
                  <TileLayer
                    url={getMapTileUrl(theme)}
                    attribution={MAP_TILES_ATTR}
                    maxZoom={20}
                  />
                  {/* Auto-fit map to show all markers */}
                  <MapAutoFit events={mappableEvents} padding={[88, 88]} singlePointZoom={16} maxZoom={16} />
                  <MapSelectionController selectedEvent={selectedEvent} markerRefs={markerRefs} />
                  <MapViewportTracker events={mappableEvents} onChange={setVisibleMapEvents} />
                  <MarkerClusterGroup
                    chunkedLoading
                    showCoverageOnHover={false}
                    maxClusterRadius={54}
                    spiderfyOnMaxZoom
                  >
                    {mappableEvents.map((event) => {
                      const CategoryIcon = getCategoryIcon(event.category);
                      const isSelected = selectedEvent?.event_id === event.event_id;
                      return (
                        <Marker
                          key={event.event_id}
                          position={event.coordinates}
                          icon={getMarkerIcon(isSelected)}
                          ref={(marker) => {
                            if (marker) {
                              markerRefs.current[event.event_id] = marker;
                            } else {
                              delete markerRefs.current[event.event_id];
                            }
                          }}
                          eventHandlers={{
                            click: () => setSelectedEvent(event),
                            popupopen: () => setSelectedEvent(event),
                            popupclose: () => setSelectedEvent((current) => (
                              current?.event_id === event.event_id ? null : current
                            ))
                          }}
                        >
                          <Popup className="custom-popup" minWidth={280} maxWidth={320}>
                            <div style={{ margin: '-1px -1px 0 -1px' }}>
                              {/* Header with category color - full width with negative margins */}
                              <div 
                                className={`px-3 py-2 ${categoryColors[event.category] || 'bg-primary'}`}
                                style={{ 
                                  margin: '0', 
                                  borderTopLeftRadius: '12px', 
                                  borderTopRightRadius: '12px' 
                                }}
                              >
                                <div className="flex items-center gap-2 text-white">
                                  <CategoryIcon className="w-4 h-4" />
                                  <span className="text-xs font-medium uppercase tracking-wide">
                                    {getCategoryName(event.category)}
                                  </span>
                                </div>
                              </div>
                              
                              {/* Content */}
                              <div className="p-[10px]">
                                <h3 className="font-bold text-base mb-2 text-gray-900 leading-tight">
                                  {event.title}
                                </h3>
                                
                                <div className="space-y-1.5 text-sm">
                                  <div className="flex items-center gap-2 text-gray-600">
                                    <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
                                    <span>{formatEventDate(event.start_date)}</span>
                                  </div>
                                  
                                  <div className="flex items-center gap-2 text-gray-600">
                                    <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                                    <span className="line-clamp-1">{event.location_name}</span>
                                  </div>
                                  
                                  {event.location_address && (
                                    <p className="text-xs text-gray-500 pl-5 line-clamp-1">
                                      {event.location_address}
                                    </p>
                                  )}
                                </div>
                                
                                {/* Action buttons */}
                                <div className="mt-3 space-y-2">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/evento/${event.event_id}`);
                                    }}
                                    className="w-full py-2 px-3 bg-gradient-to-r from-sky-500 to-blue-600 text-white text-sm font-medium rounded-lg hover:from-sky-600 hover:to-blue-700 transition-colors flex items-center justify-center gap-2"
                                  >
                                    Ver detalles
                                    <ChevronRight className="w-4 h-4" />
                                  </button>
                                  <a
                                    href={`https://www.google.com/maps/search/?api=1&query=${event.latitude},${event.longitude}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="w-full py-2 px-3 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                                  >
                                    <MapPin className="w-4 h-4 text-orange-500" />
                                    Abrir en Google Maps
                                  </a>
                                </div>
                              </div>
                            </div>
                          </Popup>
                        </Marker>
                      );
                    })}
                  </MarkerClusterGroup>
                </MapContainer>
                <MapViewportPanel events={visibleMapEvents} onSelect={(ev) => focusEventOnMap(ev)} />
                </>
              )}
            </div>
          </Card>

          <div>
            <h2 className="text-xl font-semibold mb-4">Eventos en el mapa</h2>
            {mappableEvents.length === 0 ? (
              <Card className="p-6 text-center">
                <MapPin className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground">No hay eventos con ubicación</p>
              </Card>
            ) : (
              <ScrollArea className="h-[550px]">
                <div className="flex flex-col gap-3 pr-4 pb-2 pl-2 pt-1">
                  {mappableEvents.map((event) => {
                    const CategoryIcon = getCategoryIcon(event.category);
                    const isSelected = selectedEvent?.event_id === event.event_id;
                    return (
                      <Card
                        key={event.event_id}
                        ref={(card) => {
                          if (card) {
                            cardRefs.current[event.event_id] = card;
                          } else {
                            delete cardRefs.current[event.event_id];
                          }
                        }}
                        className={`cursor-pointer transition-all duration-200 ${
                          isSelected 
                            ? 'ring-2 ring-primary shadow-lg scale-[1.02]' 
                            : 'hover:shadow-md hover:scale-[1.01]'
                        }`}
                        onClick={() => focusEventOnMap(event)}
                        data-testid={`map-event-${event.event_id}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            {/* Category icon */}
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${categoryColors[event.category] || 'bg-primary'}`}>
                              <CategoryIcon className="w-5 h-5 text-white" />
                            </div>
                            
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold mb-1 line-clamp-1">{event.title}</h3>
                              <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                                <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                                <span className="line-clamp-1">{event.location_name}</span>
                              </div>
                              <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1">
                                <Clock className="w-3 h-3 flex-shrink-0" />
                                <span>{formatEventDate(event.start_date)}</span>
                              </div>
                              <div className="mt-3 flex items-center gap-2">
                                <Button
                                  size="sm"
                                  className="h-8"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/evento/${event.event_id}`);
                                  }}
                                >
                                  Ver detalles
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-8"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    focusEventOnMap(event);
                                  }}
                                >
                                  Ver en mapa
                                </Button>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </ScrollArea>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== EVENT DETAIL PAGE ====================

const EventDetailPage = () => {
  const { user, login } = useAuth();
  const { theme } = useTheme();
  const t = useT();
  const navigate = useNavigate();
  const { eventId } = require('react-router-dom').useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    fetchEvent();
    if (user) {
      checkFavorite();
    }
  }, [eventId, user]);

  // Cargar el CSS de Leaflet para el mini-mapa de ubicación (al entrar directo a /evento/:id)
  useEffect(() => {
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css';
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }
  }, []);

  const fetchEvent = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}`);
      setEvent(response.data);
    } catch (error) {
      console.error("Error fetching event:", error);
      toast.error("Evento no encontrado");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const checkFavorite = async () => {
    try {
      const response = await axios.get(`${API}/favorites/check/${eventId}`);
      setIsFavorite(response.data.is_favorite);
    } catch (error) {
      console.error("Error checking favorite:", error);
    }
  };

  const handleToggleFavorite = async () => {
    if (!user) {
      toast.error("Inicia sesión para guardar favoritos");
      login(`/evento/${eventId}`);
      return;
    }
    if (!user.is_premium) {
      toast("Función Premium", {
        description: "Hazte Premium para guardar eventos en favoritos",
        action: {
          label: "Ver Premium",
          onClick: () => navigate("/premium")
        }
      });
      return;
    }
    try {
      if (isFavorite) {
        await axios.delete(`${API}/favorites/${eventId}`);
        setIsFavorite(false);
        toast.success("Eliminado de favoritos");
      } else {
        await axios.post(`${API}/favorites/${eventId}`);
        setIsFavorite(true);
        toast.success("Añadido a favoritos");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar favoritos");
    }
  };

  const handleAddToCalendar = async () => {
    if (!user) {
      toast.error("Inicia sesión para añadir al calendario");
      login(`/evento/${eventId}`);
      return;
    }
    if (!user.is_premium) {
      toast("Función Premium", {
        description: "Hazte Premium para añadir eventos a Google Calendar",
        action: {
          label: "Ver Premium",
          onClick: () => navigate("/premium")
        }
      });
      return;
    }
    try {
      const response = await axios.get(`${API}/events/${eventId}/calendar-link`);
      window.open(response.data.calendar_url, '_blank');
    } catch (error) {
      toast.error("Error al generar enlace de calendario");
    }
  };

  const handleRegister = async (sessionDate = null) => {
    if (!user) {
      toast.error("Debes iniciar sesión para apuntarte");
      login(`/evento/${eventId}`);
      return;
    }
    try {
      const params = sessionDate ? `?session_date=${encodeURIComponent(sessionDate)}` : '';
      await axios.post(`${API}/events/${eventId}/register${params}`);
      toast.success("¡Genial! Ya estás apuntado al evento");
      fetchEvent();
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo completar la inscripción");
    }
  };

  const handleUnregister = async (sessionDate = null) => {
    try {
      const params = sessionDate ? `?session_date=${encodeURIComponent(sessionDate)}` : '';
      await axios.delete(`${API}/events/${eventId}/register${params}`);
      toast.success("Has cancelado tu asistencia");
      fetchEvent();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al cancelar");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!event) return null;

  const CategoryIcon = getCategoryIcon(event.category);
  const eventDate = new Date(event.start_date);
  const isOwner = user?.user_id === event.user_id;
  const isPast = eventDate < new Date();
  
  // Get all images for slider (con imagen por defecto si el evento no tiene ninguna)
  const eventImages = event?.images?.length > 0 ? event.images : (event?.image_url ? [event.image_url] : [getEventImage(event)]);
  
  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % eventImages.length);
  };
  
  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + eventImages.length) % eventImages.length);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="event-detail">
      <Seo
        title={event.title}
        description={(event.description || "").slice(0, 160) || undefined}
        path={`/evento/${eventId}`}
        image={getEventImage(event)}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Event",
          name: event.title,
          startDate: event.start_date,
          ...(event.end_date ? { endDate: event.end_date } : {}),
          eventStatus: "https://schema.org/EventScheduled",
          eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
          image: [getEventImage(event)],
          ...(event.description ? { description: event.description } : {}),
          location: {
            "@type": "Place",
            ...(event.location_name ? { name: event.location_name } : {}),
            ...(event.location_address ? { address: event.location_address } : {}),
            ...(event.latitude && event.longitude
              ? { geo: { "@type": "GeoCoordinates", latitude: event.latitude, longitude: event.longitude } }
              : {}),
          },
          organizer: { "@type": "Organization", name: "Pobleo", url: SITE_URL },
        }}
      />
      {/* Hero with Image Slider */}
      <div className="relative h-64 md:h-96 overflow-hidden">
        {eventImages.length > 0 ? (
          <>
            <img 
              src={eventImages[currentImageIndex].startsWith('/api') ? `${BACKEND_URL}${eventImages[currentImageIndex]}` : eventImages[currentImageIndex]} 
              alt={event.title} 
              className="w-full h-full object-cover transition-opacity duration-300" 
            />
            {/* Slider controls */}
            {eventImages.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors"
                  aria-label="Imagen anterior"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors"
                  aria-label="Imagen siguiente"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
                {/* Dots indicator */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                  {eventImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        index === currentImageIndex ? 'bg-white' : 'bg-white/50'
                      }`}
                      aria-label={`Ir a imagen ${index + 1}`}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        ) : (
          <div className={`w-full h-full flex items-center justify-center ${categoryColors[event.category] || 'bg-primary'}`}>
            <CategoryIcon className="w-24 h-24 text-white/50" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-background to-transparent" />
        <div className="absolute top-4 left-4 right-4 flex justify-between">
          <Button
            variant="ghost"
            className="glass"
            onClick={() => navigate(-1)}
            data-testid="back-btn"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            {t("ev.back")}
          </Button>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="icon"
              className={`glass ${isFavorite ? 'text-amber-500' : ''}`}
              onClick={handleToggleFavorite}
              data-testid="favorite-btn"
            >
              <Star className={`w-5 h-5 ${isFavorite ? 'fill-current' : ''}`} />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="glass"
              onClick={handleAddToCalendar}
              data-testid="calendar-btn"
            >
              <CalendarPlus className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-8 -mt-20 relative z-10">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <Card>
              <CardContent className="p-6 md:p-8">
                <Badge className="mb-4 capitalize">{getCategoryName(event.category)}</Badge>
                <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">{event.title}</h1>

                {/* Date & Time */}
                <div className="flex flex-wrap gap-4 mb-6">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Calendar className="w-5 h-5" />
                    {format(eventDate, "EEEE, d 'de' MMMM yyyy", { locale: es })}
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Clock className="w-5 h-5" />
                    {format(eventDate, "HH:mm")}
                  </div>
                </div>

                {/* Location */}
                {event.location_name && (
                  <div className="mb-6">
                    <div className="flex items-start gap-2 text-muted-foreground">
                      <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-medium text-foreground">{event.location_name}</p>
                        {event.location_address && <p className="text-sm">{event.location_address}</p>}
                      </div>
                    </div>
                    {/* Google Maps Link - Free feature */}
                    {(event.latitude && event.longitude) && (
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${event.latitude},${event.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-gradient-to-r from-sky-500 to-blue-600 text-white text-sm font-medium rounded-full hover:from-sky-600 hover:to-blue-700 transition-all shadow-md hover:shadow-lg"
                        data-testid="google-maps-link"
                      >
                        <MapPin className="w-4 h-4" />
                        {t("ev.openMaps")}
                        <ChevronRight className="w-4 h-4" />
                      </a>
                    )}
                    {/* Mapa con la ubicación del evento */}
                    {getEventCoordinates(event) && (
                      <div className="mt-4 rounded-xl overflow-hidden border h-64" data-testid="event-detail-map">
                        <MapContainer
                          center={getEventCoordinates(event)}
                          zoom={16}
                          maxZoom={20}
                          scrollWheelZoom={false}
                          style={{ height: "100%", width: "100%" }}
                        >
                          <TileLayer
                            url={getMapTileUrl(theme)}
                            attribution={MAP_TILES_ATTR}
                            maxZoom={20}
                          />
                          <Marker position={getEventCoordinates(event)}>
                            <Popup>{event.location_name || event.location_address}</Popup>
                          </Marker>
                        </MapContainer>
                      </div>
                    )}
                  </div>
                )}

                {/* Description */}
                {event.description && (
                  <div className="prose dark:prose-invert max-w-none">
                    <h3 className="text-lg font-semibold mb-2">{t("ev.description")}</h3>
                    <p className="text-muted-foreground whitespace-pre-wrap">{event.description}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Premium Features */}
            <Card className="border-amber-500/20 bg-amber-500/5">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Crown className="w-5 h-5 text-amber-500" />
                  <span className="font-semibold text-amber-600 dark:text-amber-400">{t("ev.premiumFeatures")}</span>
                </div>
                <div className="space-y-2">
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={handleToggleFavorite}
                    data-testid="sidebar-favorite-btn"
                  >
                    <Star className={`w-4 h-4 mr-2 ${isFavorite ? 'fill-amber-500 text-amber-500' : ''}`} />
                    {isFavorite ? t("ev.inFav") : t("ev.saveFav")}
                    {!user?.is_premium && <Lock className="w-3 h-3 ml-auto text-amber-500" />}
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={handleAddToCalendar}
                    data-testid="sidebar-calendar-btn"
                  >
                    <CalendarPlus className="w-4 h-4 mr-2" />
                    {t("ev.addCalendar")}
                    {!user?.is_premium && <Lock className="w-3 h-3 ml-auto text-amber-500" />}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Actions */}
            <Card>
              <CardContent className="p-6">
                {isOwner ? (
                  <div className="space-y-3">
                    <Button 
                      className="w-full rounded-full" 
                      onClick={() => navigate(`/edit-event/${event.event_id}`)}
                      data-testid="edit-event-btn"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      {t("ev.edit")}
                    </Button>
                    <Button 
                      variant="outline" 
                      className="w-full rounded-full"
                      onClick={() => navigate(`/event/${event.event_id}/attendees`)}
                      data-testid="view-attendees-btn"
                    >
                      <Users className="w-4 h-4 mr-2" />
                      {t("ev.attendees")} ({event.attendee_count || 0})
                    </Button>
                    
                    {/* Owner attendance - with session picker for multiple sessions */}
                    {!isPast && event.upcoming_sessions?.length > 0 ? (
                      <div className="pt-3 border-t">
                        <h4 className="text-sm font-medium text-muted-foreground mb-2">{t("ev.myAttendance")}</h4>
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                          {event.upcoming_sessions.map((session) => {
                            const sessionDate = new Date(session.date);
                            return (
                              <div 
                                key={session.date}
                                className={`p-2 rounded-lg border text-sm ${
                                  session.is_attending 
                                    ? 'bg-green-50 border-green-200' 
                                    : 'bg-muted/30'
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <span>{format(sessionDate, "EEE d MMM - HH:mm", { locale: es })}</span>
                                  {session.is_attending ? (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleUnregister(session.date)}
                                      className="h-7 text-xs text-red-500 hover:text-red-600"
                                    >
                                      <X className="w-3 h-3 mr-1" />
                                      {t("ev.remove")}
                                    </Button>
                                  ) : (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleRegister(session.date)}
                                      className="h-7 text-xs"
                                    >
                                      <Check className="w-3 h-3 mr-1" />
                                      {t("ev.willAttendShort")}
                                    </Button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : !isPast && (
                      /* Non-recurring event - simple toggle */
                      event.is_attending ? (
                        <Button
                          variant="ghost"
                          className="w-full rounded-full text-muted-foreground"
                          onClick={() => handleUnregister()}
                          data-testid="owner-unregister-btn"
                        >
                          <Check className="w-4 h-4 mr-2 text-green-500" />
                          {t("ev.willAttendMine")}
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          className="w-full rounded-full"
                          onClick={() => handleRegister()}
                          data-testid="owner-register-btn"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          {t("ev.willAttend")}
                        </Button>
                      )
                    )}
                  </div>
                ) : isPast && !event.upcoming_sessions?.length ? (
                  <p className="text-center text-muted-foreground">{t("ev.past")}</p>
                ) : event.upcoming_sessions?.length > 0 ? (
                  /* Multiple sessions event - show session picker */
                  <div className="space-y-3">
                    <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                      {t("ev.chooseSession")}
                    </h3>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {event.upcoming_sessions.map((session) => {
                        const sessionDate = new Date(session.date);
                        return (
                          <div 
                            key={session.date}
                            className={`p-3 rounded-lg border transition-colors ${
                              session.is_attending 
                                ? 'bg-primary/10 border-primary' 
                                : session.is_full 
                                  ? 'bg-muted opacity-60' 
                                  : 'hover:bg-muted cursor-pointer'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium">
                                  {format(sessionDate, "EEEE d 'de' MMMM", { locale: es })}
                                </p>
                                <p className="text-sm text-muted-foreground">
                                  {format(sessionDate, "HH:mm")}h · {session.attendee_count} {t("ev.signedUp")}
                                  {event.max_attendees && ` / ${event.max_attendees}`}
                                </p>
                              </div>
                              {session.is_attending ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleUnregister(session.date)}
                                  className="text-red-500 hover:text-red-600"
                                >
                                  <X className="w-4 h-4 mr-1" />
                                  {t("ev.cancel")}
                                </Button>
                              ) : session.is_full ? (
                                <span className="text-sm text-muted-foreground">{t("ev.full")}</span>
                              ) : (
                                <Button
                                  size="sm"
                                  onClick={() => user ? handleRegister(session.date) : login(`/evento/${eventId}`)}
                                >
                                  <Check className="w-4 h-4 mr-1" />
                                  {t("ev.signUp")}
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {!user && (
                      <p className="text-sm text-muted-foreground text-center">
                        {t("ev.loginToSession")}
                      </p>
                    )}
                  </div>
                ) : event.is_attending ? (
                  <Button
                    variant="outline"
                    className="w-full rounded-full"
                    onClick={() => handleUnregister()}
                    data-testid="unregister-btn"
                  >
                    <X className="w-4 h-4 mr-2" />
                    {t("ev.cantGo")}
                  </Button>
                ) : (
                  <Button
                    className="w-full rounded-full"
                    onClick={() => handleRegister()}
                    data-testid="register-btn"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    {user ? t("ev.imIn") : t("ev.loginToSignUp")}
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Organizer */}
            {event.organizer && (
              <Card 
                className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => navigate(`/organizador/${event.user_id}`)}
                data-testid="organizer-card"
              >
                <CardContent className="p-6">
                  <h3 className="font-semibold mb-3">{t("ev.organizer")}</h3>
                  <div className="flex items-center gap-3">
                    <Avatar>
                      <AvatarImage src={event.organizer.picture} />
                      <AvatarFallback>{event.organizer.name?.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <p className="font-medium">{event.organizer.name}</p>
                      <p className="text-sm text-primary flex items-center gap-1">
                        {t("ev.seeAllEvents")}
                        <ChevronRight className="w-3 h-3" />
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Attendees */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold">{t("ev.attendeesTitle")}</h3>
                  <Badge variant="secondary">{event.attendee_count || 0}</Badge>
                </div>
                {event.max_attendees && (
                  <p className="text-sm text-muted-foreground mt-2">
                    {t("ev.maxPeople", { n: event.max_attendees })}
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

// ==================== DASHBOARD ====================

const Dashboard = () => {
  const { user } = useAuth();
  const t = useT();
  const location = useLocation();
  const currentUser = location.state?.user || user;
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [myEvents, setMyEvents] = useState([]);
  const [stats, setStats] = useState({ created: 0, registered: 0, upcoming: 0 });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [eventsRes, myEventsRes, registrationsRes] = await Promise.all([
        axios.get(`${API}/events`),
        axios.get(`${API}/my-events`),
        axios.get(`${API}/my-registrations`)
      ]);

      const upcoming = eventsRes.data.filter(e => new Date(e.start_date) > new Date()).slice(0, 6);
      setUpcomingEvents(upcoming);
      setMyEvents(myEventsRes.data.slice(0, 3));
      setStats({
        created: myEventsRes.data.length,
        registered: registrationsRes.data.length,
        upcoming: upcoming.length
      });
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard">
      <div className="container mx-auto px-4 md:px-8 py-8">
        {/* Welcome */}
        <div className="mb-8 animate-fadeIn">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">
            {t("dash.hello", { name: currentUser?.name?.split(' ')[0] || "" })}
          </h1>
          <p className="text-muted-foreground">{t("dash.summary")}</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {[
            { label: t("dash.created"), value: stats.created, icon: Calendar },
            { label: t("dash.attendances"), value: stats.registered, icon: Users },
            { label: t("dash.upcoming"), value: stats.upcoming, icon: Clock },
          ].map((stat, i) => (
            <Card key={i} className="animate-fadeIn" style={{ animationDelay: `${i * 0.1}s` }} data-testid={`stat-${i}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-muted-foreground text-sm mb-1">{stat.label}</p>
                    <p className="text-3xl font-bold">{stat.value}</p>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <stat.icon className="w-6 h-6 text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* My Events */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">{t("dash.recentEvents")}</h2>
            <Link to="/my-events" className="text-primary hover:underline text-sm">{t("dash.seeAll")}</Link>
          </div>
          {myEvents.length === 0 ? (
            <Card className="p-8 text-center">
              <p className="text-muted-foreground mb-4">{t("dash.noEvents")}</p>
              <Link to="/create-event">
                <Button className="rounded-full">
                  <Plus className="w-4 h-4 mr-2" />
                  {t("dash.createFirst")}
                </Button>
              </Link>
            </Card>
          ) : (
            <div className="space-y-3">
              {myEvents.map((event, i) => (
                <EventCard key={event.event_id} event={event} index={i} viewMode="list" />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== CREATE/EDIT EVENT ====================

const EventForm = ({ editMode = false, eventId: eventIdProp, asModal = false, onClose }) => {
  const navigate = useNavigate();
  const t = useT();
  const { eventId: routeEventId } = require('react-router-dom').useParams();
  const eventId = eventIdProp ?? routeEventId;
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [autocomplete, setAutocomplete] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "general",
    start_date: new Date(),
    end_date: null,
    location_name: "",
    location_address: "",
    municipality: "",
    latitude: null,
    longitude: null,
    image_url: "",
    max_attendees: null,
    is_public: true,
    // Recurrence fields
    is_recurring: false,
    recurrence_mode: "continuous", // "continuous" (curso) or "multiple_sessions" (teatro)
    recurrence_type: "weekly",
    recurrence_days: [],
    recurrence_end_date: null,
    recurrence_time: "18:00",
    images: []  // Array of image URLs
  });
  const [uploadingImages, setUploadingImages] = useState(false);

  useEffect(() => {
    fetchCategories();
    if (editMode && eventId) {
      fetchEvent();
    }
  }, [editMode, eventId]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(registerCategories(response.data));
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchEvent = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}`);
      const event = response.data;
      setFormData({
        ...event,
        start_date: new Date(event.start_date),
        end_date: event.end_date ? new Date(event.end_date) : null,
        recurrence_end_date: event.recurrence_end_date ? new Date(event.recurrence_end_date) : null,
        recurrence_days: event.recurrence_days || [],
        images: event.images || []
      });
    } catch (error) {
      console.error("Error fetching event:", error);
      navigate("/my-events");
    }
  };

  const onPlaceChanged = () => {
    if (autocomplete) {
      const place = autocomplete.getPlace();
      if (place.geometry) {
        // Desglosar dirección y población a partir de los componentes de Google
        const components = place.address_components || [];
        const get = (type) => components.find((c) => c.types.includes(type));

        // Población: locality, con respaldos
        const municipality =
          get("locality")?.long_name ||
          get("postal_town")?.long_name ||
          get("administrative_area_level_2")?.long_name ||
          "";

        // Dirección (solo calle + número, sin población)
        const route = get("route")?.long_name || "";
        const streetNumber = get("street_number")?.long_name || "";
        const street = [route, streetNumber].filter(Boolean).join(" ").trim();
        const address = street || place.formatted_address || "";

        setFormData(prev => ({
          ...prev,
          // No pisar el nombre del lugar si el usuario ya lo rellenó (lleva más detalle y es lo que se muestra)
          location_name: prev.location_name?.trim() ? prev.location_name : (place.name || ""),
          location_address: address,
          municipality,
          latitude: place.geometry.location.lat(),
          longitude: place.geometry.location.lng()
        }));
      }
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploadingImages(true);
    try {
      const formDataUpload = new FormData();
      files.forEach(file => formDataUpload.append('files', file));
      
      const response = await axios.post(`${API}/upload-multiple`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...response.data.urls]
      }));
      toast.success(`${response.data.urls.length} imagen(es) subida(s)`);
    } catch (error) {
      toast.error("Error al subir las imágenes");
    } finally {
      setUploadingImages(false);
    }
  };

  const removeImage = (indexToRemove) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, index) => index !== indexToRemove)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Create payload with proper date conversions
      const payload = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        start_date: formData.start_date?.toISOString(),
        end_date: formData.end_date?.toISOString() || null,
        location_name: formData.location_name,
        location_address: formData.location_address,
        municipality: formData.municipality,
        latitude: formData.latitude,
        longitude: formData.longitude,
        image_url: formData.images?.[0] || formData.image_url,  // First image as main
        images: formData.images,
        max_attendees: formData.max_attendees || null,
        is_public: formData.is_public,
        // Recurrence fields
        is_recurring: formData.is_recurring,
        recurrence_mode: formData.recurrence_mode,
        recurrence_type: formData.recurrence_type,
        recurrence_days: formData.recurrence_days,
        recurrence_end_date: formData.recurrence_end_date?.toISOString() || null,
        recurrence_time: formData.recurrence_time
      };

      if (editMode) {
        await axios.put(`${API}/events/${eventId}`, payload);
        toast.success("Evento actualizado");
      } else {
        await axios.post(`${API}/events`, payload);
        toast.success("Evento creado");
      }
      if (onClose) {
        onClose(true); // modal: cerrar indicando que SÍ se guardó (para refrescar el listado)
      } else if (editMode) {
        navigate(-1); // página: volver al listado anterior
      } else {
        navigate("/my-events");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("¿Estás seguro de eliminar este evento?")) return;
    
    try {
      await axios.delete(`${API}/events/${eventId}`);
      toast.success("Evento eliminado");
      if (onClose) onClose(true); else navigate("/my-events");
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  return (
    <div className={asModal ? "" : "min-h-screen bg-background"} data-testid="event-form">
      <div className={asModal ? "" : "container mx-auto px-4 md:px-8 py-8"}>
        <div className={asModal ? "" : "max-w-2xl mx-auto"}>
          {!asModal && (
            <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4" data-testid="back-btn">
              <ChevronLeft className="w-4 h-4 mr-2" />
              {t("ev.back")}
            </Button>
          )}

          <Card className={asModal ? "border-0 shadow-none" : ""}>
            {!asModal && (
              <CardHeader>
                <CardTitle>{editMode ? t("form.editTitle") : t("form.createTitle")}</CardTitle>
                <CardDescription>
                  {editMode ? t("form.editSub") : t("form.createSub")}
                </CardDescription>
              </CardHeader>
            )}
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Dos columnas: izquierda datos del evento, derecha ubicación (en móvil se apilan) */}
                <div className="grid md:grid-cols-2 gap-6 items-start">
                {/* ===== Columna izquierda: datos del evento ===== */}
                <div className="space-y-6">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">{t("form.eventData")}</h3>
                {/* Title */}
                <div>
                  <Label htmlFor="title">{t("form.title")}</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder={t("form.placeholderTitle")}
                    required
                    data-testid="title-input"
                  />
                </div>

                {/* Description */}
                <div>
                  <Label htmlFor="description">{t("form.desc")}</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder={t("form.placeholderDesc")}
                    rows={4}
                    data-testid="description-input"
                  />
                </div>

                {/* Category */}
                <div>
                  <Label>{t("filter.category")}</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                    <SelectTrigger data-testid="category-select">
                      <SelectValue placeholder={t("form.selectCategory")} />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Date & Time */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{t("form.startDate")}</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start" data-testid="start-date-picker">
                          <Calendar className="w-4 h-4 mr-2" />
                          {format(formData.start_date, "d MMM yyyy", { locale: es })}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <CalendarPicker
                          mode="single"
                          selected={formData.start_date}
                          onSelect={(date) => date && setFormData({ ...formData, start_date: date })}
                          locale={es}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label>{t("form.hour")}</Label>
                    <Input
                      type="time"
                      value={format(formData.start_date, "HH:mm")}
                      onChange={(e) => {
                        const [hours, minutes] = e.target.value.split(':');
                        const newDate = new Date(formData.start_date);
                        newDate.setHours(parseInt(hours), parseInt(minutes));
                        setFormData({ ...formData, start_date: newDate });
                      }}
                      data-testid="start-time-input"
                    />
                  </div>
                </div>

                </div>
                {/* ===== Columna derecha: ubicación ===== */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">{t("form.locationTitle")}</h3>
                  {/* Location with Google Places Autocomplete */}
                  <div>
                    <Label>{t("form.searchLocation")}</Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground z-10" />
                      <Autocomplete
                        onLoad={setAutocomplete}
                        onPlaceChanged={onPlaceChanged}
                        options={{
                          types: ['establishment', 'geocode'],
                          componentRestrictions: { country: 'es' }
                        }}
                      >
                        <Input
                          placeholder={t("form.searchPh")}
                          className="pl-10"
                          data-testid="location-search"
                        />
                      </Autocomplete>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {t("form.searchHint")}
                    </p>
                  </div>

                  {/* Location Name */}
                  <div>
                    <Label htmlFor="location_name">{t("form.placeName")}</Label>
                    <Input
                      id="location_name"
                      value={formData.location_name}
                      onChange={(e) => setFormData({ ...formData, location_name: e.target.value })}
                      placeholder={t("form.placeNamePh")}
                      data-testid="location-input"
                    />
                  </div>

                  {/* Address + Municipality */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="location_address">{t("form.address")}</Label>
                      <Input
                        id="location_address"
                        value={formData.location_address}
                        onChange={(e) => setFormData({ ...formData, location_address: e.target.value })}
                        placeholder={t("form.addressPh")}
                        data-testid="address-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="municipality">{t("filter.town")}</Label>
                      <Input
                        id="municipality"
                        value={formData.municipality || ""}
                        readOnly
                        placeholder={t("form.townFilled")}
                        className="bg-muted/50 cursor-not-allowed"
                        data-testid="municipality-input"
                      />
                      <p className="text-xs text-muted-foreground mt-1">{t("form.townHint")}</p>
                    </div>
                  </div>

                  {/* Coordinates (auto-filled but editable) */}
                  {(formData.latitude || formData.longitude) && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="latitude">{t("form.lat")}</Label>
                        <Input
                          id="latitude"
                          type="number"
                          step="any"
                          value={formData.latitude || ""}
                          onChange={(e) => setFormData({ ...formData, latitude: e.target.value ? parseFloat(e.target.value) : null })}
                          placeholder="40.4168"
                          data-testid="latitude-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="longitude">{t("form.lng")}</Label>
                        <Input
                          id="longitude"
                          type="number"
                          step="any"
                          value={formData.longitude || ""}
                          onChange={(e) => setFormData({ ...formData, longitude: e.target.value ? parseFloat(e.target.value) : null })}
                          placeholder="-3.7038"
                          data-testid="longitude-input"
                        />
                      </div>
                    </div>
                  )}

                  {/* Máximo de asistentes (aquí para aprovechar el hueco de la columna) */}
                  <div>
                    <Label htmlFor="max_attendees">{t("form.maxAttendees")}</Label>
                    <Input
                      id="max_attendees"
                      type="number"
                      value={formData.max_attendees || ""}
                      onChange={(e) => setFormData({ ...formData, max_attendees: e.target.value ? parseInt(e.target.value) : null })}
                      placeholder={t("form.noLimit")}
                      data-testid="max-attendees-input"
                    />
                  </div>
                </div>
                </div>{/* ===== fin: dos columnas ===== */}

                {/* Image Upload */}
                <div className="space-y-3">
                  <Label>{t("form.images")}</Label>
                  
                  {/* Upload button */}
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 px-4 py-2 bg-muted hover:bg-muted/80 rounded-lg cursor-pointer transition-colors">
                      <Upload className="w-4 h-4" />
                      <span className="text-sm">{uploadingImages ? t("form.uploading") : t("form.uploadImages")}</span>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleImageUpload}
                        className="hidden"
                        disabled={uploadingImages}
                        data-testid="image-upload-input"
                      />
                    </label>
                    <span className="text-sm text-muted-foreground">
                      {t("form.imagesCount", { n: formData.images?.length || 0 })}
                    </span>
                  </div>
                  
                  {/* Image preview grid */}
                  {formData.images?.length > 0 && (
                    <div className="grid grid-cols-4 gap-2">
                      {formData.images.map((url, index) => (
                        <div key={index} className="relative group aspect-square">
                          <img 
                            src={url.startsWith('/api') ? `${BACKEND_URL}${url}` : url} 
                            alt={`Imagen ${index + 1}`}
                            className="w-full h-full object-cover rounded-lg"
                          />
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                          >
                            <X className="w-4 h-4" />
                          </button>
                          {index === 0 && (
                            <span className="absolute bottom-1 left-1 text-xs bg-primary text-white px-2 py-0.5 rounded">
                              {t("form.main")}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Recurrence Section */}
                <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-base font-semibold">{t("form.recurring")}</Label>
                      <p className="text-sm text-muted-foreground">{t("form.recurringSub")}</p>
                    </div>
                    <Switch
                      checked={formData.is_recurring}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_recurring: checked })}
                      data-testid="recurring-switch"
                    />
                  </div>
                  
                  {formData.is_recurring && (
                    <div className="space-y-4 pt-2">
                      {/* Recurrence Mode */}
                      <div className="space-y-2">
                        <Label className="mb-2 block">{t("form.recurrenceType")}</Label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <label
                            className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                              formData.recurrence_mode === "continuous"
                                ? "bg-primary/10 border-primary"
                                : "bg-background hover:bg-muted"
                            }`}
                          >
                            <input
                              type="radio"
                              name="recurrence_mode"
                              value="continuous"
                              checked={formData.recurrence_mode === "continuous"}
                              onChange={(e) => setFormData({ ...formData, recurrence_mode: e.target.value })}
                              className="mt-1"
                            />
                            <div>
                              <p className="font-medium">{t("form.continuous")}</p>
                              <p className="text-xs text-muted-foreground">{t("form.continuousD")}</p>
                            </div>
                          </label>
                          <label
                            className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                              formData.recurrence_mode === "multiple_sessions"
                                ? "bg-primary/10 border-primary"
                                : "bg-background hover:bg-muted"
                            }`}
                          >
                            <input
                              type="radio"
                              name="recurrence_mode"
                              value="multiple_sessions"
                              checked={formData.recurrence_mode === "multiple_sessions"}
                              onChange={(e) => setFormData({ ...formData, recurrence_mode: e.target.value })}
                              className="mt-1"
                            />
                            <div>
                              <p className="font-medium">{t("form.multiple")}</p>
                              <p className="text-xs text-muted-foreground">{t("form.multipleD")}</p>
                            </div>
                          </label>
                        </div>
                      </div>
                      
                      {/* Days of week */}
                      <div>
                        <Label className="mb-2 block">{t("form.weekdays")}</Label>
                        <div className="flex flex-wrap gap-2">
                          {[
                            { id: "monday", label: t("day.mon") },
                            { id: "tuesday", label: t("day.tue") },
                            { id: "wednesday", label: t("day.wed") },
                            { id: "thursday", label: t("day.thu") },
                            { id: "friday", label: t("day.fri") },
                            { id: "saturday", label: t("day.sat") },
                            { id: "sunday", label: t("day.sun") }
                          ].map((day) => (
                            <label
                              key={day.id}
                              className={`flex items-center justify-center w-12 h-10 rounded-lg border cursor-pointer transition-colors ${
                                formData.recurrence_days.includes(day.id)
                                  ? "bg-primary text-primary-foreground border-primary"
                                  : "bg-background hover:bg-muted"
                              }`}
                            >
                              <input
                                type="checkbox"
                                className="sr-only"
                                checked={formData.recurrence_days.includes(day.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setFormData({
                                      ...formData,
                                      recurrence_days: [...formData.recurrence_days, day.id]
                                    });
                                  } else {
                                    setFormData({
                                      ...formData,
                                      recurrence_days: formData.recurrence_days.filter(d => d !== day.id)
                                    });
                                  }
                                }}
                              />
                              <span className="text-sm font-medium">{day.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      
                      {/* Time */}
                      <div>
                        <Label htmlFor="recurrence_time">{t("form.eventHour")}</Label>
                        <Input
                          id="recurrence_time"
                          type="time"
                          value={formData.recurrence_time}
                          onChange={(e) => setFormData({ ...formData, recurrence_time: e.target.value })}
                          className="w-32"
                          data-testid="recurrence-time"
                        />
                      </div>
                      
                      {/* End Date */}
                      <div>
                        <Label>{t("form.lastDate")}</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="w-full justify-start mt-1" data-testid="recurrence-end-date">
                              <Calendar className="w-4 h-4 mr-2" />
                              {formData.recurrence_end_date 
                                ? format(formData.recurrence_end_date, "PPP", { locale: es })
                                : t("form.selectLastDate")
                              }
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <CalendarPicker
                              mode="single"
                              selected={formData.recurrence_end_date}
                              onSelect={(date) => setFormData({ ...formData, recurrence_end_date: date })}
                              locale={es}
                              disabled={(date) => date < formData.start_date}
                            />
                          </PopoverContent>
                        </Popover>
                        <p className="text-xs text-muted-foreground mt-1">
                          <strong>{t("form.important")}</strong> {t("form.importantD")}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Public Toggle */}
                <div className="flex items-center justify-between">
                  <div>
                    <Label>{t("form.public")}</Label>
                    <p className="text-sm text-muted-foreground">{t("form.publicSub")}</p>
                  </div>
                  <Switch
                    checked={formData.is_public}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_public: checked })}
                    data-testid="public-switch"
                  />
                </div>

                {/* Actions */}
                <div className={`flex gap-3 pt-4 ${asModal ? "sticky bottom-0 z-10 -mx-6 -mb-6 border-t bg-background px-6 pb-4" : ""}`}>
                  <Button type="submit" className="flex-1 rounded-full" disabled={loading} data-testid="submit-btn">
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t("form.saving")}
                      </>
                    ) : editMode ? t("form.saveChanges") : t("nav.createEvent")}
                  </Button>
                  {editMode && (
                    <Button type="button" variant="destructive" onClick={handleDelete} className="rounded-full" data-testid="delete-btn">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                  {asModal && (
                    <Button type="button" variant="outline" onClick={() => onClose && onClose()} className="rounded-full">
                      {t("ev.cancel")}
                    </Button>
                  )}
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// ==================== MY EVENTS PAGE ====================

const MyEventsPage = () => {
  const t = useT();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingEventId, setEditingEventId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchMyEvents();
  }, []);

  const fetchMyEvents = async () => {
    try {
      const response = await axios.get(`${API}/my-events`);
      setEvents(response.data);
    } catch (error) {
      console.error("Error fetching my events:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="my-events-page">
      <div className="container mx-auto px-4 md:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">{t("nav.myEvents")}</h1>
          <Button onClick={() => navigate("/create-event")} className="rounded-full" data-testid="create-btn">
            <Plus className="w-4 h-4 mr-2" />
            {t("list.newEvent")}
          </Button>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <Card key={i} className="animate-pulse">
                <div className="flex">
                  <div className="w-20 h-24 bg-muted" />
                  <div className="flex-1 p-4">
                    <div className="h-4 bg-muted rounded w-20 mb-2" />
                    <div className="h-6 bg-muted rounded w-3/4 mb-2" />
                    <div className="h-4 bg-muted rounded w-1/2" />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : events.length === 0 ? (
          <Card className="p-12 text-center">
            <Calendar className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold mb-2">{t("list.noEvents")}</h3>
            <p className="text-muted-foreground mb-4">{t("list.createFirstSub")}</p>
            <Button onClick={() => navigate("/create-event")} className="rounded-full">
              <Plus className="w-4 h-4 mr-2" />
              {t("nav.createEvent")}
            </Button>
          </Card>
        ) : (
          <div className="space-y-3">
            {events.map((event, i) => (
              <EventCard key={event.event_id} event={event} index={i} viewMode="list" onEdit={(ev) => setEditingEventId(ev.event_id)} />
            ))}
          </div>
        )}
      </div>

      {/* Modal de edición (refresca la lista al cerrar) */}
      <Dialog open={!!editingEventId} onOpenChange={(open) => { if (!open) { setEditingEventId(null); fetchMyEvents(); } }}>
        <DialogContent
          className="w-[calc(100%-1.5rem)] max-w-6xl max-h-[90vh] overflow-y-auto"
          onInteractOutside={(e) => {
            // No cerrar el modal al clicar en el desplegable de Google Places
            const target = e.detail?.originalEvent?.target;
            if (target?.closest?.(".pac-container")) e.preventDefault();
          }}
        >
          <DialogHeader>
            <DialogTitle>{t("form.editTitle")}</DialogTitle>
          </DialogHeader>
          {editingEventId && (
            <EventForm
              editMode
              asModal
              eventId={editingEventId}
              onClose={() => setEditingEventId(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== MY REGISTRATIONS PAGE ====================

const MyRegistrationsPage = () => {
  const t = useT();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRegistrations();
  }, []);

  const fetchRegistrations = async () => {
    try {
      const response = await axios.get(`${API}/my-registrations`);
      setEvents(response.data);
    } catch (error) {
      console.error("Error fetching registrations:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="registrations-page">
      <div className="container mx-auto px-4 md:px-8 py-8">
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-8">{t("nav.myRegistrations")}</h1>

        {loading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <Card key={i} className="animate-pulse">
                <div className="flex">
                  <div className="w-20 h-24 bg-muted" />
                  <div className="flex-1 p-4">
                    <div className="h-4 bg-muted rounded w-20 mb-2" />
                    <div className="h-6 bg-muted rounded w-3/4 mb-2" />
                    <div className="h-4 bg-muted rounded w-1/2" />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : events.length === 0 ? (
          <Card className="p-12 text-center">
            <Users className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold mb-2">{t("reg.none")}</h3>
            <p className="text-muted-foreground">{t("reg.noneSub")}</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {events.map((event, i) => (
              <EventCard key={event.event_id} event={event} index={i} viewMode="list" />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== FAVORITES PAGE ====================

const FavoritesPage = () => {
  const { user, login } = useAuth();
  const t = useT();
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      login('/favoritos');
      return;
    }
    fetchFavorites();
  }, [user]);

  const fetchFavorites = async () => {
    try {
      const response = await axios.get(`${API}/favorites`);
      if (response.data.is_premium) {
        setFavorites(response.data.favorites);
      }
    } catch (error) {
      console.error("Error fetching favorites:", error);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user.is_premium) {
    return (
      <div className="min-h-screen bg-background" data-testid="favorites-page">
        <div className="container mx-auto px-4 md:px-8 py-8">
          <Card className="max-w-lg mx-auto text-center p-8">
            <div className="w-16 h-16 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto mb-4">
              <Crown className="w-8 h-8 text-amber-500" />
            </div>
            <h1 className="text-2xl font-bold mb-2">{t("fav.premiumTitle")}</h1>
            <p className="text-muted-foreground mb-6">
              {t("fav.premiumSub")}
            </p>
            <Button onClick={() => navigate("/premium")} className="rounded-full bg-amber-500 hover:bg-amber-600">
              <Crown className="w-4 h-4 mr-2" />
              {t("nav.goPremium")}
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="favorites-page">
      <div className="container mx-auto px-4 md:px-8 py-8">
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-8">{t("fav.myTitle")}</h1>

        {loading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <Card key={i} className="animate-pulse">
                <div className="flex">
                  <div className="w-20 h-24 bg-muted" />
                  <div className="flex-1 p-4">
                    <div className="h-4 bg-muted rounded w-20 mb-2" />
                    <div className="h-6 bg-muted rounded w-3/4 mb-2" />
                    <div className="h-4 bg-muted rounded w-1/2" />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : favorites.length === 0 ? (
          <Card className="p-12 text-center">
            <Star className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold mb-2">{t("fav.none")}</h3>
            <p className="text-muted-foreground mb-4">{t("fav.noneSub")}</p>
            <Button onClick={() => navigate("/")} className="rounded-full">
              {t("fav.explore")}
            </Button>
          </Card>
        ) : (
          <div className="space-y-3">
            {favorites.map((event, i) => (
              <EventCard key={event.event_id} event={event} index={i} viewMode="list" />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== PREMIUM PAGE ====================

const PremiumPage = () => {
  const { user, login } = useAuth();
  const t = useT();
  const navigate = useNavigate();

  const features = [
    { icon: Star, title: t("prem.f1t"), desc: t("prem.f1d") },
    { icon: CalendarPlus, title: t("prem.f2t"), desc: t("prem.f2d") },
    { icon: Bell, title: t("prem.f3t"), desc: t("prem.f3d") },
    { icon: Crown, title: t("prem.f4t"), desc: t("prem.f4d") },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="premium-page">
      {/* Hero */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-500/20 via-transparent to-orange-500/20" />
        <div className="container mx-auto px-4 md:px-8 relative">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-20 h-20 rounded-full bg-amber-500 flex items-center justify-center mx-auto mb-6">
              <Crown className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
              {t("prem.title")}
            </h1>
            <p className="text-xl text-muted-foreground mb-8">
              {t("prem.sub")}
            </p>
            <div className="inline-flex items-baseline gap-1 mb-8">
              <span className="text-5xl font-bold">€4.99</span>
              <span className="text-muted-foreground">{t("prem.perMonth")}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16">
        <div className="container mx-auto px-4 md:px-8">
          <h2 className="text-2xl font-semibold text-center mb-12">{t("prem.whatH2")}</h2>
          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {features.map((feature, i) => (
              <Card key={i} className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-6 h-6 text-amber-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground">{feature.desc}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4 md:px-8">
          <Card className="max-w-lg mx-auto p-8 text-center border-amber-500/20">
            <h3 className="text-xl font-semibold mb-4">{t("prem.soonH3")}</h3>
            <p className="text-muted-foreground mb-6">
              {t("prem.soonSub")}
            </p>
            {user ? (
              <Button disabled className="rounded-full bg-amber-500 hover:bg-amber-600 opacity-50">
                <Lock className="w-4 h-4 mr-2" />
                {t("prem.soonBtn")}
              </Button>
            ) : (
              <Button onClick={() => login('/premium')} className="rounded-full">
                {t("nav.login")}
              </Button>
            )}
          </Card>
        </div>
      </section>
    </div>
  );
};

// ==================== ORGANIZER PROFILE PAGE ====================

const OrganizerProfilePage = () => {
  const navigate = useNavigate();
  const t = useT();
  const { userId } = require('react-router-dom').useParams();
  const [organizer, setOrganizer] = useState(null);
  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Default filter: from today to 60 days ahead
  const getDefaultFilters = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const sixtyDaysLater = new Date(today);
    sixtyDaysLater.setDate(sixtyDaysLater.getDate() + 60);
    return {
      category: "all",
      search: "",
      startDate: today,
      endDate: sixtyDaysLater
    };
  };
  
  const [filters, setFilters] = useState(getDefaultFilters);

  useEffect(() => {
    fetchOrganizer();
    fetchCategories();
  }, [userId]);

  useEffect(() => {
    if (organizer) {
      fetchEvents();
    }
  }, [filters, organizer]);

  const fetchOrganizer = async () => {
    try {
      const response = await axios.get(`${API}/organizers/${userId}`);
      setOrganizer(response.data);
    } catch (error) {
      console.error("Error fetching organizer:", error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(registerCategories(response.data));
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.category && filters.category !== "all") params.append("category", filters.category);
      if (filters.search) params.append("search", filters.search);
      if (filters.startDate) params.append("start_date", filters.startDate.toISOString());
      if (filters.endDate) params.append("end_date", filters.endDate.toISOString());

      const response = await axios.get(`${API}/organizers/${userId}/events?${params.toString()}`);
      setEvents(response.data);
    } catch (error) {
      console.error("Error fetching events:", error);
    } finally {
      setLoading(false);
    }
  };

  if (!organizer) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="organizer-profile-page">
      {/* Header with organizer info */}
      <div className="bg-gradient-to-r from-sky-500 to-blue-600 text-white">
        <div className="container mx-auto px-4 md:px-8 py-8">
          <Button 
            variant="ghost" 
            className="mb-4 text-white hover:bg-white/20"
            onClick={() => navigate(-1)}
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            {t("ev.back")}
          </Button>

          <div className="flex items-center gap-4">
            <Avatar className="w-20 h-20 border-4 border-white/30">
              <AvatarImage src={organizer.picture} />
              <AvatarFallback className="text-2xl bg-white/20">{organizer.name?.charAt(0)}</AvatarFallback>
            </Avatar>
            <div>
              <h1 className="text-3xl font-bold">{organizer.name}</h1>
              <p className="text-white/80 flex items-center gap-2 mt-1">
                <Calendar className="w-4 h-4" />
                {t("op.published", { n: organizer.event_count })}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-8 py-8">
        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  className="pl-9"
                  placeholder={t("filter.searchEvents")}
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  data-testid="organizer-search"
                />
              </div>

              {/* Category Filter */}
              <Select value={filters.category} onValueChange={(v) => setFilters({ ...filters, category: v })}>
                <SelectTrigger className="w-full md:w-48" data-testid="organizer-category-filter">
                  <SelectValue placeholder={t("filter.category")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("filter.allCategories")}</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Date Range */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full md:w-56 justify-start" data-testid="organizer-date-filter">
                    <Calendar className="w-4 h-4 mr-2" />
                    {filters.startDate ? format(filters.startDate, "d MMM", { locale: es }) : "Fecha"}
                    {filters.endDate && ` - ${format(filters.endDate, "d MMM", { locale: es })}`}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarPicker
                    mode="range"
                    defaultMonth={filters.startDate || filters.endDate || new Date()}
                    selected={{ from: filters.startDate, to: filters.endDate }}
                    onSelect={(range) => setFilters({ ...filters, startDate: range?.from, endDate: range?.to })}
                    locale={es}
                  />
                  <div className="flex items-end gap-2 p-3 border-t">
                    <div className="flex-1">
                      <label className="text-xs text-muted-foreground mb-1 block">Desde (hora)</label>
                      <Input type="time" value={filters.startTime || "00:00"} onChange={(e) => setFilters({ ...filters, startTime: e.target.value })} className="h-9" />
                    </div>
                    <div className="flex-1">
                      <label className="text-xs text-muted-foreground mb-1 block">Hasta (hora)</label>
                      <Input type="time" value={filters.endTime || "23:59"} onChange={(e) => setFilters({ ...filters, endTime: e.target.value })} className="h-9" />
                    </div>
                  </div>
                </PopoverContent>
              </Popover>

              {/* Clear Filters */}
              {(filters.category !== "all" || filters.poblacion !== "all" || filters.search) && (
                <Button
                  variant="ghost"
                  onClick={() => setFilters(getDefaultFilters())}
                  data-testid="organizer-clear-filters"
                >
                  <X className="w-4 h-4 mr-2" />
                  Limpiar
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Events List */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Próximos Eventos</h2>
          
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : events.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Calendar className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold mb-2">{t("op.noEvents")}</h3>
                <p className="text-muted-foreground">{t("op.adjustFilters")}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {events.map((event, index) => (
                <EventCard key={event.event_id} event={event} index={index} viewMode="list" />
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

// ==================== ATTENDEES PAGE ====================

const AttendeesPage = () => {
  const navigate = useNavigate();
  const t = useT();
  const { eventId } = require('react-router-dom').useParams();
  const [attendees, setAttendees] = useState([]);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState("all");

  useEffect(() => {
    fetchData();
  }, [eventId]);

  const fetchData = async () => {
    try {
      const [eventRes, attendeesRes] = await Promise.all([
        axios.get(`${API}/events/${eventId}`),
        axios.get(`${API}/events/${eventId}/attendees`)
      ]);
      setEvent(eventRes.data);
      setAttendees(attendeesRes.data);
    } catch (error) {
      console.error("Error:", error);
      toast.error("Error al cargar datos");
      navigate("/my-events");
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveAttendee = async (attendeeId, attendeeName) => {
    if (!window.confirm(`¿Estás seguro de eliminar a ${attendeeName} del evento?`)) {
      return;
    }
    try {
      await axios.delete(`${API}/events/${eventId}/attendees/${attendeeId}`);
      toast.success("Asistente eliminado");
      fetchData(); // Refresh list
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar asistente");
    }
  };

  // Check if event has multiple sessions
  const isMultipleSessions = event?.is_recurring && event?.recurrence_mode === "multiple_sessions";
  
  // Group attendees by session
  const attendeesBySession = attendees.reduce((acc, att) => {
    const session = att.session_date || "general";
    if (!acc[session]) acc[session] = [];
    acc[session].push(att);
    return acc;
  }, {});
  
  // Get unique sessions sorted by date
  const sessions = Object.keys(attendeesBySession).sort();
  
  // Filter attendees based on selection
  const filteredAttendees = selectedSession === "all" 
    ? attendees 
    : attendeesBySession[selectedSession] || [];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="attendees-page">
      <div className="container mx-auto px-4 md:px-8 py-8 flex-1">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4" data-testid="back-btn">
          <ChevronLeft className="w-4 h-4 mr-2" />
          {t("ev.back")}
        </Button>

        <div className="max-w-2xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle>{t("att.of", { title: event?.title || "" })}</CardTitle>
              <CardDescription>
                {isMultipleSessions
                  ? t("att.totalReg", { n: attendees.length })
                  : t("att.people", { n: attendees.length })
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Session filter for multiple sessions events */}
              {isMultipleSessions && sessions.length > 0 && (
                <div className="mb-6">
                  <Label className="mb-2 block text-sm font-medium">{t("att.filterSession")}</Label>
                  <Select value={selectedSession} onValueChange={setSelectedSession}>
                    <SelectTrigger data-testid="session-filter">
                      <SelectValue placeholder={t("att.allSessions")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">
                        {t("att.allSessions")} ({attendees.length})
                      </SelectItem>
                      {sessions.filter(s => s !== "general").map((session) => {
                        const sessionDate = new Date(session);
                        const count = attendeesBySession[session]?.length || 0;
                        return (
                          <SelectItem key={session} value={session}>
                            {format(sessionDate, "EEEE d 'de' MMMM - HH:mm", { locale: es })} ({count})
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {filteredAttendees.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  {selectedSession === "all"
                    ? t("att.noneYet")
                    : t("att.noneSession")
                  }
                </p>
              ) : (
                <div className="space-y-3">
                  {filteredAttendees.map((attendee) => (
                    <div 
                      key={attendee.attendee_id} 
                      className="flex items-center gap-3 p-3 rounded-lg bg-muted/50" 
                      data-testid={`attendee-${attendee.attendee_id}`}
                    >
                      <Avatar>
                        <AvatarImage src={attendee.user?.picture} />
                        <AvatarFallback>{attendee.user?.name?.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{attendee.user?.name}</p>
                        <p className="text-sm text-muted-foreground truncate">{attendee.user?.email}</p>
                      </div>
                      {isMultipleSessions && attendee.session_date && selectedSession === "all" && (
                        <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full whitespace-nowrap">
                          {format(new Date(attendee.session_date), "d MMM HH:mm", { locale: es })}
                        </span>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveAttendee(attendee.attendee_id, attendee.user?.name)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50 shrink-0"
                        data-testid={`remove-attendee-${attendee.attendee_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      <Footer />
    </div>
  );
};

// ==================== ADMIN PANEL ====================

const AdminPanel = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState({ totalUsers: 0, totalEvents: 0, totalRegistrations: 0, premiumUsers: 0 });
  const [analytics, setAnalytics] = useState(null);
  const [categories, setCategories] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [pendingEvents, setPendingEvents] = useState([]);
  const [subscriptionAnalytics, setSubscriptionAnalytics] = useState(null);
  const [premiumUsers, setPremiumUsers] = useState([]);
  const [platformSettings, setPlatformSettings] = useState({});
  const [editingSettings, setEditingSettings] = useState({});
  const [activeTab, setActiveTab] = useState("events");
  const [loading, setLoading] = useState(true);
  const [discoverySources, setDiscoverySources] = useState([]);
  const [discoveredEvents, setDiscoveredEvents] = useState([]);
  const [discoveryLoading, setDiscoveryLoading] = useState(false);
  const [bootstrapLoading, setBootstrapLoading] = useState(false);
  const [instagramLoading, setInstagramLoading] = useState(false);
  const [discoveryRunStatus, setDiscoveryRunStatus] = useState(null);
  const [discoveryRunConfig, setDiscoveryRunConfig] = useState({
    max_queries_per_source: 3,
    max_results_per_query: 5,
    max_pages_per_source: 12,
    persist_to_app_events: true,
  });
  const [instagramForm, setInstagramForm] = useState({
    url: "",
    municipality: "",
    caption_text: "",
  });
  const [instagramPreview, setInstagramPreview] = useState([]);
  // Filtros del listado de eventos (admin)
  const [eventFilters, setEventFilters] = useState({ organizer: "all", category: "all", poblacion: "all", search: "" });
  // Selección para borrado múltiple
  const [selectedEventIds, setSelectedEventIds] = useState([]);
  // Resultado de la última importación (para mostrar estado de población)
  const [importResult, setImportResult] = useState(null);
  // Edición de evento en modal
  const [editingEventId, setEditingEventId] = useState(null);
  // Último evento abierto para editar (se mantiene resaltado en el listado para localizarlo)
  const [lastEditedEventId, setLastEditedEventId] = useState(null);

  useEffect(() => {
    if (user?.role !== "admin") {
      return;
    }
    fetchAdminData();
  }, [user?.role]);

  useEffect(() => {
    if (user?.role !== "admin" || activeTab !== "import") {
      return;
    }
    fetchDiscoveryData();
  }, [activeTab, user?.role]);

  useEffect(() => {
    if (user?.role !== "admin" || activeTab !== "import" || discoveryRunStatus?.status !== "running") {
      return;
    }

    const intervalId = window.setInterval(async () => {
      try {
        const response = await axios.get(`${API}/admin/discovery/runs/${discoveryRunStatus.run_id}`);
        setDiscoveryRunStatus(response.data);

        if (response.data?.status === "completed" || response.data?.status === "failed") {
          fetchDiscoveryData();
          fetchAdminData();
        }
      } catch (error) {
        console.error("Error polling discovery run status:", error);
      }
    }, 5000);

    return () => window.clearInterval(intervalId);
  }, [activeTab, discoveryRunStatus?.run_id, discoveryRunStatus?.status, user?.role]);

  const fetchAdminData = async (silent = false) => {
    try {
      if (!silent) setLoading(true);  // modo silencioso: refresca sin ocultar el listado con el spinner
      const [usersRes, eventsRes, analyticsRes, categoriesRes, pendingRes, subRes, settingsRes, premiumRes] = await Promise.all([
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/events`),
        axios.get(`${API}/admin/analytics`),
        axios.get(`${API}/admin/categories`),
        axios.get(`${API}/admin/events/moderation/pending`),
        axios.get(`${API}/admin/subscriptions/analytics`),
        axios.get(`${API}/admin/settings`),
        axios.get(`${API}/admin/subscriptions/users`)
      ]);

      setUsers(usersRes.data.users || []);
      setEvents(eventsRes.data.events || []);
      setAnalytics(analyticsRes.data);
      setCategories(registerCategories(categoriesRes.data.categories || []));
      setPendingEvents(pendingRes.data.events || []);
      setSubscriptionAnalytics(subRes.data);
      setPlatformSettings(settingsRes.data);
      setEditingSettings({...settingsRes.data});
      setPremiumUsers(premiumRes.data.users || []);
      setStats(usersRes.data.stats || { totalUsers: 0, totalEvents: 0, totalRegistrations: 0, premiumUsers: 0 });
    } catch (error) {
      console.error("Error fetching admin data:", error);
      toast.error("Error al cargar datos de administración");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleToggleRole = async (userId, newRole) => {
    try {
      await axios.patch(`${API}/admin/users/${userId}/role`, { role: newRole });
      toast.success(`Rol actualizado a ${newRole}`);
      fetchAdminData();
    } catch (error) {
      console.error("Error updating role:", error);
      toast.error("Error al actualizar el rol");
    }
  };

  const handleTogglePremium = async (userId, isPremium) => {
    try {
      await axios.patch(`${API}/admin/users/${userId}/premium`, { is_premium: !isPremium });
      toast.success(isPremium ? "Premium desactivado" : "Premium activado");
      fetchAdminData();
    } catch (error) {
      console.error("Error updating premium:", error);
      toast.error("Error al actualizar estado premium");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("¿Estás seguro de que quieres eliminar este usuario?")) {
      return;
    }
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success("Usuario eliminado");
      fetchAdminData();
    } catch (error) {
      console.error("Error deleting user:", error);
      toast.error("Error al eliminar el usuario");
    }
  };

  const handleImportEvents = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(`${API}/admin/import-events`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const result = response.data;
      setImportResult(result);
      const summary = [
        result.created ? `${result.created} creados` : null,
        result.updated ? `${result.updated} actualizados` : null,
      ].filter(Boolean).join(" · ") || "0 nuevos";
      toast.success(`${summary} (de ${result.total})`);

      if (result.failed > 0) {
        const failedMessages = result.failed_events
          ?.slice(0, 3)
          .map(f => `${f.title}: ${f.error}`)
          .join("\n");
        toast.error(`${result.failed} eventos fallaron:\n${failedMessages}`);
      }

      const processed = [...(result.created_events || []), ...(result.updated_events || [])];
      const corrected = processed.filter(ev => ev.municipality_status === "corrected").length;
      const unrecognized = processed.filter(ev => ev.municipality_status === "unrecognized").length;
      if (corrected > 0) toast.info(`${corrected} población(es) autocorregida(s)`);
      if (unrecognized > 0) toast.warning(`${unrecognized} sin población reconocida`);

      fetchAdminData();
      e.target.value = "";
    } catch (error) {
      console.error("Error importing events:", error);
      toast.error("Error al importar eventos");
    }
  };

  const fetchDiscoveryData = async () => {
    try {
      setDiscoveryLoading(true);
      const [sourcesRes, eventsRes, latestRunRes] = await Promise.all([
        axios.get(`${API}/admin/discovery/sources`),
        axios.get(`${API}/admin/discovery/events?limit=50`),
        axios.get(`${API}/admin/discovery/runs/latest`),
      ]);

      setDiscoverySources(sourcesRes.data.sources || []);
      setDiscoveredEvents(eventsRes.data.events || []);
      setDiscoveryRunStatus(latestRunRes.data.run || null);
    } catch (error) {
      console.error("Error fetching discovery data:", error);
      toast.error("Error al cargar la búsqueda automática");
    } finally {
      setDiscoveryLoading(false);
    }
  };

  const handleBootstrapDiscovery = async () => {
    try {
      setBootstrapLoading(true);
      const response = await axios.post(`${API}/admin/discovery/bootstrap-marina-alta`);
      toast.success(`Fuentes preparadas: ${response.data.seeded_sources}`);
      fetchDiscoveryData();
    } catch (error) {
      console.error("Error bootstrapping discovery sources:", error);
      toast.error("Error al preparar las fuentes de Marina Alta");
    } finally {
      setBootstrapLoading(false);
    }
  };

  const handleRunDiscovery = async () => {
    try {
      setDiscoveryLoading(true);
      const response = await axios.post(`${API}/admin/discovery/run`, discoveryRunConfig);
      setDiscoveryRunStatus(response.data);
      toast.success("Crawler iniciado en segundo plano. Se irá actualizando el estado automáticamente.");
      fetchDiscoveryData();
    } catch (error) {
      console.error("Error running discovery crawler:", error);
      toast.error("Error al ejecutar el crawler de Marina Alta");
    } finally {
      setDiscoveryLoading(false);
    }
  };

  const handleExtractInstagram = async () => {
    if (!instagramForm.url && !instagramForm.caption_text.trim()) {
      toast.error("Añade una URL pública o un caption de Instagram");
      return;
    }

    try {
      setInstagramLoading(true);
      const payload = {
        url: instagramForm.url || undefined,
        municipality: instagramForm.municipality || undefined,
        caption_text: instagramForm.caption_text || undefined,
      };
      const response = await axios.post(`${API}/discovery/extract/instagram`, payload);
      setInstagramPreview(response.data || []);
      toast.success(`Instagram analizado: ${(response.data || []).length} eventos detectados`);
    } catch (error) {
      console.error("Error extracting Instagram events:", error);
      toast.error("Error al analizar Instagram público");
    } finally {
      setInstagramLoading(false);
    }
  };

  const isDiscoveryRunning = discoveryRunStatus?.status === "running";

  const handleEditEvent = (eventId) => {
    navigate(`/edit-event/${eventId}`);
  };

  const handleDeleteEvent = async (eventId, title) => {
    if (!window.confirm(`¿Estás seguro de que quieres eliminar "${title}"?`)) {
      return;
    }
    try {
      await axios.delete(`${API}/admin/events/${eventId}`);
      toast.success("Evento eliminado");
      fetchAdminData();
    } catch (error) {
      console.error("Error deleting event:", error);
      toast.error("Error al eliminar el evento");
    }
  };

  const handleBulkDeleteEvents = async () => {
    if (selectedEventIds.length === 0) return;
    if (!window.confirm(`¿Eliminar ${selectedEventIds.length} evento(s) seleccionado(s)? Esta acción no se puede deshacer.`)) {
      return;
    }
    try {
      const results = await Promise.allSettled(
        selectedEventIds.map((id) => axios.delete(`${API}/admin/events/${id}`))
      );
      const ok = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.length - ok;
      if (ok > 0) toast.success(`${ok} evento(s) eliminado(s)`);
      if (failed > 0) toast.error(`${failed} no se pudieron eliminar`);
      setSelectedEventIds([]);
      fetchAdminData();
    } catch (error) {
      console.error("Error deleting events:", error);
      toast.error("Error al eliminar los eventos");
    }
  };

  const handleBulkSetCategory = async (categoryId) => {
    if (selectedEventIds.length === 0 || !categoryId) return;
    try {
      const res = await axios.post(`${API}/admin/events/bulk-category`, {
        event_ids: selectedEventIds,
        category: categoryId,
      });
      toast.success(`Categoría asignada a ${res.data.updated} evento(s)`);
      setSelectedEventIds([]);
      fetchAdminData(true); // refresco silencioso (no oculta el listado)
    } catch (error) {
      console.error("Error setting category:", error);
      toast.error(error.response?.data?.detail || "Error al asignar la categoría");
    }
  };

  const handleToggleBlock = async (userId, isBlocked) => {
    try {
      await axios.patch(`${API}/admin/users/${userId}/block`, { is_blocked: !isBlocked });
      toast.success(isBlocked ? "Usuario desbloqueado" : "Usuario bloqueado");
      fetchAdminData();
    } catch (error) {
      console.error("Error blocking user:", error);
      toast.error("Error al bloquear/desbloquear usuario");
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error("El nombre de la categoría es requerido");
      return;
    }
    try {
      await axios.post(`${API}/admin/categories`, { name: newCategoryName });
      toast.success("Categoría creada");
      setNewCategoryName("");
      fetchAdminData();
    } catch (error) {
      console.error("Error creating category:", error);
      toast.error("Error al crear categoría");
    }
  };

  const handleDeleteCategory = async (categoryId, categoryName) => {
    if (!window.confirm(`¿Estás seguro de que quieres eliminar la categoría "${categoryName}"?`)) {
      return;
    }
    try {
      await axios.delete(`${API}/admin/categories/${categoryId}`);
      toast.success("Categoría eliminada");
      fetchAdminData();
    } catch (error) {
      console.error("Error deleting category:", error);
      toast.error(error.response?.data?.detail || "Error al eliminar categoría");
    }
  };

  const handleModerateEvent = async (eventId, status, notes) => {
    try {
      await axios.patch(`${API}/admin/events/${eventId}/moderation`, {
        status: status,
        notes: notes
      });
      toast.success(`Evento ${status === 'approved' ? 'aprobado' : 'rechazado'}`);
      fetchAdminData();
    } catch (error) {
      console.error("Error moderating event:", error);
      toast.error("Error al moderar evento");
    }
  };

  const handleSaveSettings = async () => {
    try {
      await axios.patch(`${API}/admin/settings`, editingSettings);
      toast.success("Configuración guardada");
      setPlatformSettings(editingSettings);
      fetchAdminData();
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Error al guardar la configuración");
    }
  };

  if (user?.role !== "admin") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card>
          <CardContent className="pt-6">
            <p className="text-red-600 font-semibold">❌ Acceso denegado</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ----- Listado de eventos (admin): opciones de filtro y eventos filtrados -----
  const getOrganizerName = (uid) => users.find((u) => u.user_id === uid)?.name || "Desconocido";

  const organizerOptions = Array.from(
    new Map(events.map((e) => [e.user_id, getOrganizerName(e.user_id)])).entries()
  ).sort((a, b) => a[1].localeCompare(b[1]));

  const categoryOptions = Array.from(new Set(events.map((e) => e.category || "general"))).sort();

  const poblacionOptions = Array.from(
    new Set(events.map((e) => (e.municipality || "").trim()).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b));
  const hasEventsWithoutPoblacion = events.some((e) => !(e.municipality || "").trim());

  const filteredEvents = events.filter((e) => {
    const poblacion = (e.municipality || "").trim();
    if (eventFilters.organizer !== "all" && e.user_id !== eventFilters.organizer) return false;
    if (eventFilters.category !== "all" && (e.category || "general") !== eventFilters.category) return false;
    if (eventFilters.poblacion === "__none__" && poblacion) return false;
    if (eventFilters.poblacion !== "all" && eventFilters.poblacion !== "__none__" && poblacion !== eventFilters.poblacion) return false;
    if (eventFilters.search && !(e.title || "").toLowerCase().includes(eventFilters.search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 md:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2 flex items-center gap-2">
            <Shield className="w-8 h-8 text-red-600" />
            Panel Admin
          </h1>
          <p className="text-muted-foreground">Gestión de usuarios y suscripciones</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Usuarios Total</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalUsers}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Premium</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-amber-500">{stats.premiumUsers}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Eventos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalEvents}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Registros</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalRegistrations}</div>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="flex-wrap">
            <TabsTrigger value="events">Eventos ({events.length})</TabsTrigger>
            <TabsTrigger value="categories">Categorías</TabsTrigger>
            <TabsTrigger value="analytics">Analytics</TabsTrigger>
            <TabsTrigger value="users">Usuarios ({users.length})</TabsTrigger>
            <TabsTrigger value="subscriptions">Premium ({stats.premiumUsers})</TabsTrigger>
            <TabsTrigger value="moderation">Moderación ({pendingEvents.length})</TabsTrigger>
            <TabsTrigger value="revenue">Ingresos</TabsTrigger>
            <TabsTrigger value="settings">Config</TabsTrigger>
            <TabsTrigger value="import">Importar</TabsTrigger>
          </TabsList>

          <TabsContent value="users" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Gestión de Usuarios</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin" />
                  </div>
                ) : users.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 px-4">Usuario</th>
                          <th className="text-left py-2 px-4">Email</th>
                          <th className="text-left py-2 px-4">Rol</th>
                          <th className="text-left py-2 px-4">Premium</th>
                          <th className="text-left py-2 px-4">Bloqueado</th>
                          <th className="text-left py-2 px-4">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((u) => (
                          <tr key={u.user_id} className={`border-b hover:bg-muted/50 ${u.is_blocked ? 'opacity-60' : ''}`}>
                            <td className="py-2 px-4 font-medium">{u.name}</td>
                            <td className="py-2 px-4 text-muted-foreground">{u.email}</td>
                            <td className="py-2 px-4">
                              <Select value={u.role || "user"} onValueChange={(newRole) => handleToggleRole(u.user_id, newRole)}>
                                <SelectTrigger className="w-24">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="user">Usuario</SelectItem>
                                  <SelectItem value="organizer">Organizador</SelectItem>
                                  <SelectItem value="admin">Admin</SelectItem>
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="py-2 px-4">
                              <Button
                                variant={u.is_premium ? "default" : "outline"}
                                size="sm"
                                onClick={() => handleTogglePremium(u.user_id, u.is_premium)}
                                className={u.is_premium ? "bg-amber-500 hover:bg-amber-600" : ""}
                              >
                                {u.is_premium ? "✓" : "Activar"}
                              </Button>
                            </td>
                            <td className="py-2 px-4">
                              <Button
                                variant={u.is_blocked ? "destructive" : "outline"}
                                size="sm"
                                onClick={() => handleToggleBlock(u.user_id, u.is_blocked)}
                              >
                                {u.is_blocked ? "Bloqueado" : "Activo"}
                              </Button>
                            </td>
                            <td className="py-2 px-4">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive"
                                onClick={() => handleDeleteUser(u.user_id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">No hay usuarios</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="subscriptions" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Usuarios Premium</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin" />
                  </div>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {users.filter(u => u.is_premium).length > 0 ? (
                      users.filter(u => u.is_premium).map((u) => (
                        <div key={u.user_id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                          <div>
                            <p className="font-medium">{u.name}</p>
                            <p className="text-xs text-muted-foreground">{u.email}</p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleTogglePremium(u.user_id, u.is_premium)}
                          >
                            Desactivar
                          </Button>
                        </div>
                      ))
                    ) : (
                      <p className="text-muted-foreground text-center py-8">No hay premium</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="events" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Gestión de Eventos</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin" />
                  </div>
                ) : events.length > 0 ? (
                  <>
                    {/* Filtros */}
                    <div className="flex flex-col md:flex-row md:flex-wrap gap-3 mb-4">
                      <div className="relative flex-1 min-w-[180px]">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          placeholder="Buscar por título..."
                          value={eventFilters.search}
                          onChange={(e) => setEventFilters({ ...eventFilters, search: e.target.value })}
                          className="pl-10"
                          data-testid="admin-event-search"
                        />
                      </div>
                      <Select value={eventFilters.organizer} onValueChange={(v) => setEventFilters({ ...eventFilters, organizer: v })}>
                        <SelectTrigger className="w-full md:w-48"><SelectValue placeholder="Organizador" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos los organizadores</SelectItem>
                          {organizerOptions.map(([uid, name]) => (
                            <SelectItem key={uid} value={uid}>{name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select value={eventFilters.category} onValueChange={(v) => setEventFilters({ ...eventFilters, category: v })}>
                        <SelectTrigger className="w-full md:w-44"><SelectValue placeholder="Categoría" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todas las categorías</SelectItem>
                          {categoryOptions.map((cat) => (
                            <SelectItem key={cat} value={cat}>{getCategoryName(cat)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select value={eventFilters.poblacion} onValueChange={(v) => setEventFilters({ ...eventFilters, poblacion: v })}>
                        <SelectTrigger className="w-full md:w-52"><SelectValue placeholder="Población" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todas las poblaciones</SelectItem>
                          {poblacionOptions.map((p) => (
                            <SelectItem key={p} value={p}>{p}</SelectItem>
                          ))}
                          {hasEventsWithoutPoblacion && <SelectItem value="__none__">Sin población</SelectItem>}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex items-center justify-between mb-2 gap-2">
                      <p className="text-xs text-muted-foreground">
                        {filteredEvents.length} de {events.length} eventos
                        {selectedEventIds.length > 0 && ` · ${selectedEventIds.length} seleccionado(s)`}
                      </p>
                      <div className="flex items-center gap-2">
                        {selectedEventIds.length > 0 && (
                          <Select value="" onValueChange={handleBulkSetCategory}>
                            <SelectTrigger className="w-48 h-9" data-testid="bulk-category-select">
                              <SelectValue placeholder={`Asignar categoría (${selectedEventIds.length})`} />
                            </SelectTrigger>
                            <SelectContent>
                              {categories.map((cat) => (
                                <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        {selectedEventIds.length > 0 && (
                          <Button variant="destructive" size="sm" onClick={handleBulkDeleteEvents} data-testid="bulk-delete-events">
                            <Trash2 className="w-4 h-4 mr-1" /> Eliminar seleccionados ({selectedEventIds.length})
                          </Button>
                        )}
                        {(eventFilters.organizer !== "all" || eventFilters.category !== "all" || eventFilters.poblacion !== "all" || eventFilters.search) && (
                          <Button variant="ghost" size="sm" onClick={() => setEventFilters({ organizer: "all", category: "all", poblacion: "all", search: "" })}>
                            <X className="w-4 h-4 mr-1" /> Limpiar filtros
                          </Button>
                        )}
                      </div>
                    </div>

                    {filteredEvents.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b">
                              <th className="py-2 px-4 w-8">
                                <Checkbox
                                  checked={filteredEvents.length > 0 && filteredEvents.every((e) => selectedEventIds.includes(e.event_id))}
                                  onCheckedChange={(checked) => {
                                    const ids = filteredEvents.map((e) => e.event_id);
                                    setSelectedEventIds(
                                      checked
                                        ? Array.from(new Set([...selectedEventIds, ...ids]))
                                        : selectedEventIds.filter((id) => !ids.includes(id))
                                    );
                                  }}
                                  aria-label="Seleccionar todos"
                                />
                              </th>
                              <th className="text-left py-2 px-4">Título</th>
                              <th className="text-left py-2 px-4">Organizador</th>
                              <th className="text-left py-2 px-4">Categoría</th>
                              <th className="text-left py-2 px-4">Población</th>
                              <th className="text-left py-2 px-4">Fecha Inicio</th>
                              <th className="text-left py-2 px-4">Registros</th>
                              <th className="text-left py-2 px-4">Acciones</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredEvents.map((event) => (
                              <tr key={event.event_id} className={`border-b hover:bg-muted/50 ${event.event_id === lastEditedEventId ? "bg-primary/10 ring-1 ring-inset ring-primary/40" : selectedEventIds.includes(event.event_id) ? "bg-muted/40" : ""}`}>
                                <td className="py-3 px-4">
                                  <Checkbox
                                    checked={selectedEventIds.includes(event.event_id)}
                                    onCheckedChange={(checked) => {
                                      setSelectedEventIds(
                                        checked
                                          ? [...selectedEventIds, event.event_id]
                                          : selectedEventIds.filter((id) => id !== event.event_id)
                                      );
                                    }}
                                    aria-label={`Seleccionar ${event.title}`}
                                  />
                                </td>
                                <td className="py-3 px-4 font-medium truncate max-w-xs">{event.title}</td>
                                <td className="py-3 px-4 text-muted-foreground text-xs truncate">
                                  {getOrganizerName(event.user_id)}
                                </td>
                                <td className="py-3 px-4">
                                  <span className="capitalize text-xs">{getCategoryName(event.category || "general")}</span>
                                </td>
                                <td className="py-3 px-4 text-xs text-muted-foreground truncate max-w-[12rem]">
                                  {event.municipality || "—"}
                                </td>
                                <td className="py-3 px-4 text-xs">
                                  {event.start_date ? new Date(event.start_date).toLocaleDateString('es-ES') : "-"}
                                </td>
                                <td className="py-3 px-4 text-xs text-center">
                                  {event.max_attendees ? `${event.registered_count || 0}/${event.max_attendees}` : "∞"}
                                </td>
                                <td className="py-3 px-4 flex gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => { setEditingEventId(event.event_id); setLastEditedEventId(event.event_id); }}
                                    data-testid={`edit-event-${event.event_id}`}
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="destructive"
                                    size="sm"
                                    onClick={() => handleDeleteEvent(event.event_id, event.title)}
                                    data-testid={`delete-event-${event.event_id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-center py-8">No hay eventos que coincidan con los filtros</p>
                    )}
                  </>
                ) : (
                  <p className="text-muted-foreground text-center py-8">No hay eventos</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="import" className="mt-6">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Importar Eventos desde JSON</CardTitle>
                  <p className="text-sm text-muted-foreground mt-2">
                    Sube un archivo JSON con un array de eventos. Cada evento se creará como propio tuyo.
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="border-2 border-dashed rounded-lg p-6 text-center hover:bg-muted/50 transition-colors">
                      <input
                        type="file"
                        accept=".json"
                        onChange={handleImportEvents}
                        className="hidden"
                        id="json-file-input"
                        data-testid="import-json-input"
                      />
                      <label htmlFor="json-file-input" className="cursor-pointer">
                        <div className="flex flex-col items-center gap-2">
                          <Upload className="w-8 h-8 text-muted-foreground" />
                          <p className="font-medium">Haz clic para seleccionar un archivo JSON</p>
                          <p className="text-xs text-muted-foreground">O arrastra tu archivo aquí</p>
                        </div>
                      </label>
                    </div>

                    <div className="bg-muted p-4 rounded-lg space-y-2">
                      <p className="font-medium text-sm">Estructura esperada:</p>
                      <pre className="text-xs overflow-x-auto">
{`[
  {
    "title": "Mi Evento",
    "start_date": "2026-03-15T20:00:00",
    "end_date": "2026-03-15T23:00:00",
    "category": "music",
    "location_name": "Teatro",
    "location_address": "Calle Principal",
    "municipality": "Pedreguer",
    "latitude": 40.4168,
    "longitude": -3.7038,
    "description": "Descripción",
    "max_attendees": 100,
    "is_public": true
  }
]`}
                      </pre>
                    </div>

                    <div className="space-y-2">
                      <p className="text-sm font-medium">Campos disponibles:</p>
                      <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                        <li><span className="font-medium">title</span> - Nombre del evento (requerido)</li>
                        <li><span className="font-medium">start_date</span> - Fecha y hora de inicio ISO format (requerido)</li>
                        <li><span className="font-medium">end_date</span> - Fecha y hora de fin (opcional)</li>
                        <li><span className="font-medium">category</span> - music, business, sports, food, arts, technology, health, education, social, general</li>
                        <li><span className="font-medium">location_name</span> - Nombre del lugar</li>
                        <li><span className="font-medium">location_address</span> - Dirección (calle)</li>
                        <li><span className="font-medium">municipality</span> (o <span className="font-medium">poblacion</span>) - Población; se verifica y autocorrige al importar</li>
                        <li><span className="font-medium">latitude/longitude</span> - Coordenadas GPS</li>
                        <li><span className="font-medium">description</span> - Descripción del evento</li>
                        <li><span className="font-medium">image_url</span> - URL de imagen principal</li>
                        <li><span className="font-medium">max_attendees</span> - Número máximo de asistentes</li>
                        <li><span className="font-medium">is_public</span> - true o false</li>
                      </ul>
                    </div>

                    {importResult && (
                      <div className="rounded-lg border p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-sm">Resultado de la última importación</p>
                          <Button variant="ghost" size="sm" onClick={() => setImportResult(null)}>
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {importResult.created} creados · {importResult.updated || 0} actualizados · {importResult.failed} fallidos de {importResult.total}
                        </p>
                        {[...(importResult.created_events || []), ...(importResult.updated_events || [])].length > 0 && (
                          <div className="overflow-x-auto max-h-72 overflow-y-auto">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="border-b text-left text-muted-foreground">
                                  <th className="py-1 pr-3">Evento</th>
                                  <th className="py-1 pr-3">Acción</th>
                                  <th className="py-1 pr-3">Población</th>
                                  <th className="py-1">Estado</th>
                                </tr>
                              </thead>
                              <tbody>
                                {[...(importResult.created_events || []), ...(importResult.updated_events || [])].map((ev) => {
                                  const map = {
                                    matched_internal: ["Reconocida", "text-emerald-600"],
                                    verified_google: ["Verificada (Google)", "text-emerald-600"],
                                    derived: ["Deducida", "text-blue-600"],
                                    corrected: [`Corregida${ev.municipality_original ? ` (${ev.municipality_original} → ${ev.municipality})` : ""}`, "text-amber-600"],
                                    unrecognized: ["No reconocida", "text-red-600"],
                                  };
                                  const [label, cls] = map[ev.municipality_status] || ["—", "text-muted-foreground"];
                                  const isUpdated = ev.status === "updated";
                                  return (
                                    <tr key={ev.event_id} className="border-b">
                                      <td className="py-1 pr-3 truncate max-w-[14rem]">{ev.title}</td>
                                      <td className={`py-1 pr-3 ${isUpdated ? "text-amber-600" : "text-emerald-600"}`}>
                                        {isUpdated ? "Actualizado" : "Creado"}
                                      </td>
                                      <td className="py-1 pr-3">{ev.municipality || "—"}</td>
                                      <td className={`py-1 ${cls}`}>{label}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-primary" />
                    Búsqueda profunda Marina Alta
                  </CardTitle>
                  <CardDescription>
                    Rastrea ayuntamientos, agendas públicas, resultados web e Instagram público, y guarda eventos con el formato JSON exacto de la API.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between rounded-lg border p-4">
                    <div>
                      <p className="font-medium">Fuentes preparadas</p>
                      <p className="text-sm text-muted-foreground">
                        {discoverySources.length > 0
                          ? `${discoverySources.length} fuentes activas para municipios de Marina Alta`
                          : "Todavía no hay fuentes sembradas"}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={fetchDiscoveryData} disabled={discoveryLoading}>
                        {discoveryLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                        Recargar
                      </Button>
                      <Button onClick={handleBootstrapDiscovery} disabled={bootstrapLoading}>
                        {bootstrapLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MapPin className="w-4 h-4 mr-2" />}
                        Preparar Marina Alta
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label>Consultas por fuente</Label>
                      <Input
                        type="number"
                        min="1"
                        max="10"
                        value={discoveryRunConfig.max_queries_per_source}
                        onChange={(e) => setDiscoveryRunConfig(prev => ({ ...prev, max_queries_per_source: Number(e.target.value) || 1 }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Resultados por consulta</Label>
                      <Input
                        type="number"
                        min="1"
                        max="10"
                        value={discoveryRunConfig.max_results_per_query}
                        onChange={(e) => setDiscoveryRunConfig(prev => ({ ...prev, max_results_per_query: Number(e.target.value) || 1 }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Páginas por fuente</Label>
                      <Input
                        type="number"
                        min="1"
                        max="30"
                        value={discoveryRunConfig.max_pages_per_source}
                        onChange={(e) => setDiscoveryRunConfig(prev => ({ ...prev, max_pages_per_source: Number(e.target.value) || 1 }))}
                      />
                    </div>
                    <div className="space-y-3 rounded-lg border p-3">
                      <div className="flex items-center justify-between gap-3">
                        <Label htmlFor="persist-discovery-events">Añadir a la app</Label>
                        <Switch
                          id="persist-discovery-events"
                          checked={discoveryRunConfig.persist_to_app_events}
                          onCheckedChange={(checked) => setDiscoveryRunConfig(prev => ({ ...prev, persist_to_app_events: checked }))}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Si está activo, los eventos detectados también se insertan en la colección principal de eventos.
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button onClick={handleRunDiscovery} disabled={discoveryLoading || bootstrapLoading || isDiscoveryRunning} data-testid="run-marina-alta-crawler">
                      {discoveryLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                      {isDiscoveryRunning ? "Crawler en ejecución" : "Ejecutar crawler profundo"}
                    </Button>
                    <Badge variant="secondary">{discoveredEvents.length} eventos detectados</Badge>
                    <Badge variant="outline">{events.length} eventos totales en app</Badge>
                  </div>

                  {discoveryRunStatus && (
                    <div className="rounded-lg border p-4 space-y-3 bg-muted/30">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                        <div>
                          <p className="font-medium">Estado del crawler</p>
                          <p className="text-sm text-muted-foreground">
                            Run {discoveryRunStatus.run_id} · {discoveryRunStatus.status === "running" ? "ejecutándose" : discoveryRunStatus.status === "completed" ? "completado" : discoveryRunStatus.status === "failed" ? "fallido" : discoveryRunStatus.status}
                          </p>
                        </div>
                        <div className="flex gap-2 items-center">
                          <Badge variant={discoveryRunStatus.status === "completed" ? "secondary" : discoveryRunStatus.status === "failed" ? "destructive" : "outline"}>
                            {discoveryRunStatus.status}
                          </Badge>
                          {isDiscoveryRunning && <Loader2 className="w-4 h-4 animate-spin" />}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                        <div className="rounded border p-3">
                          <p className="text-muted-foreground text-xs">Fuentes</p>
                          <p className="font-semibold">{discoveryRunStatus.processed_sources || 0} / {discoveryRunStatus.source_count || 0}</p>
                        </div>
                        <div className="rounded border p-3">
                          <p className="text-muted-foreground text-xs">Nuevos</p>
                          <p className="font-semibold">{discoveryRunStatus.stored || 0}</p>
                        </div>
                        <div className="rounded border p-3">
                          <p className="text-muted-foreground text-xs">Actualizados</p>
                          <p className="font-semibold">{discoveryRunStatus.updated || 0}</p>
                        </div>
                        <div className="rounded border p-3">
                          <p className="text-muted-foreground text-xs">Añadidos app</p>
                          <p className="font-semibold">{discoveryRunStatus.promoted_created || 0}</p>
                        </div>
                        <div className="rounded border p-3">
                          <p className="text-muted-foreground text-xs">Estado</p>
                          <p className="font-semibold capitalize">{discoveryRunStatus.status || "-"}</p>
                        </div>
                      </div>

                      {discoveryRunStatus.current_source_name && (
                        <p className="text-sm text-muted-foreground">
                          Procesando ahora: <span className="font-medium text-foreground">{discoveryRunStatus.current_source_name}</span>
                        </p>
                      )}

                      {discoveryRunStatus.error && (
                        <p className="text-sm text-red-600">{discoveryRunStatus.error}</p>
                      )}
                    </div>
                  )}

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div>
                        <h3 className="font-semibold mb-1">Extracción desde Instagram público</h3>
                        <p className="text-sm text-muted-foreground">
                          Pega una URL pública o un caption para extraer eventos automáticamente.
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label>URL pública de Instagram</Label>
                        <Input
                          placeholder="https://www.instagram.com/p/..."
                          value={instagramForm.url}
                          onChange={(e) => setInstagramForm(prev => ({ ...prev, url: e.target.value }))}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Municipio</Label>
                        <Input
                          placeholder="Dénia, Xàbia, Calp..."
                          value={instagramForm.municipality}
                          onChange={(e) => setInstagramForm(prev => ({ ...prev, municipality: e.target.value }))}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Caption o texto del post</Label>
                        <Textarea
                          placeholder="Concierto rock el 30/03/2026 a las 22:00 en Pego..."
                          value={instagramForm.caption_text}
                          onChange={(e) => setInstagramForm(prev => ({ ...prev, caption_text: e.target.value }))}
                          rows={5}
                        />
                      </div>

                      <Button onClick={handleExtractInstagram} disabled={instagramLoading}>
                        {instagramLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                        Analizar Instagram
                      </Button>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <h3 className="font-semibold mb-1">Vista previa del JSON exacto</h3>
                        <p className="text-sm text-muted-foreground">
                          Salida normalizada tal como la expone la API pública.
                        </p>
                      </div>

                      <div className="rounded-lg bg-muted p-4 min-h-[280px] overflow-auto">
                        <pre className="text-xs whitespace-pre-wrap break-all">
{instagramPreview.length > 0
  ? JSON.stringify(instagramPreview, null, 2)
  : `[
  {
    "title": "Concierto rock",
    "description": "Rock en directo.",
    "category": "music",
    "start_date": "2026-03-30T22:00:00",
    "end_date": "2026-03-30T23:30:00",
    "location_name": "Sala",
    "location_address": "Pego",
    "latitude": 38.844,
    "longitude": -0.118,
    "image_url": "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=500",
    "max_attendees": 300,
    "is_public": true
  }
]`}
                        </pre>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="font-semibold">Últimos eventos detectados</h3>
                      <p className="text-xs text-muted-foreground">Se muestran los 50 más recientes cargados en admin</p>
                    </div>

                    {discoveryLoading && discoveredEvents.length === 0 ? (
                      <div className="flex justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin" />
                      </div>
                    ) : discoveredEvents.length > 0 ? (
                      <div className="overflow-x-auto rounded-lg border">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b bg-muted/50">
                              <th className="text-left py-3 px-4">Título</th>
                              <th className="text-left py-3 px-4">Municipio</th>
                              <th className="text-left py-3 px-4">Categoría</th>
                              <th className="text-left py-3 px-4">Inicio</th>
                              <th className="text-left py-3 px-4">Ubicación</th>
                            </tr>
                          </thead>
                          <tbody>
                            {discoveredEvents.map((item) => (
                              <tr key={item.payload_hash} className="border-b last:border-b-0 hover:bg-muted/30">
                                <td className="py-3 px-4 font-medium max-w-xs truncate">{item.payload?.title || item.title}</td>
                                <td className="py-3 px-4 text-muted-foreground">{item.municipality || item.payload?.location_address || "-"}</td>
                                <td className="py-3 px-4 capitalize">{item.payload?.category || item.category || "general"}</td>
                                <td className="py-3 px-4 text-xs">{item.payload?.start_date ? new Date(item.payload.start_date).toLocaleString('es-ES') : '-'}</td>
                                <td className="py-3 px-4 text-xs text-muted-foreground max-w-xs truncate">{item.payload?.location_name || item.location_name || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
                        Aún no hay eventos descubiertos. Prepara las fuentes y ejecuta el crawler.
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="analytics" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Dashboard de Analytics</CardTitle>
              </CardHeader>
              <CardContent>
                {loading || !analytics ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin" />
                  </div>
                ) : (
                  <div className="space-y-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Events by Category */}
                      <div className="border rounded-lg p-4">
                        <h3 className="font-semibold mb-4">Eventos por Categoría</h3>
                        {analytics.events_by_category && analytics.events_by_category.length > 0 ? (
                          <div className="space-y-2">
                            {analytics.events_by_category.map((cat) => (
                              <div key={cat.category} className="flex items-center justify-between">
                                <span className="capitalize text-sm">{cat.category}</span>
                                <div className="flex items-center gap-2">
                                  <div className="w-24 bg-muted rounded-full h-2">
                                    <div
                                      className="bg-blue-500 h-2 rounded-full"
                                      style={{
                                        width: `${(cat.count / Math.max(...analytics.events_by_category.map(c => c.count))) * 100}%`
                                      }}
                                    />
                                  </div>
                                  <span className="text-sm font-medium w-8 text-right">{cat.count}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-muted-foreground text-sm">No hay datos</p>
                        )}
                      </div>

                      {/* Premium Ratio */}
                      <div className="border rounded-lg p-4">
                        <h3 className="font-semibold mb-4">Usuarios Premium vs Regular</h3>
                        {analytics.premium_ratio ? (
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <span className="text-sm">Premium</span>
                              <span className="font-semibold">{analytics.premium_ratio.premium}</span>
                            </div>
                            <div className="w-full bg-muted rounded-full h-2">
                              <div
                                className="bg-amber-500 h-2 rounded-full"
                                style={{
                                  width: `${(analytics.premium_ratio.premium / analytics.premium_ratio.total) * 100}%`
                                }}
                              />
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-sm">Regular</span>
                              <span className="font-semibold">{analytics.premium_ratio.regular}</span>
                            </div>
                            <div className="w-full bg-muted rounded-full h-2">
                              <div
                                className="bg-green-500 h-2 rounded-full"
                                style={{
                                  width: `${(analytics.premium_ratio.regular / analytics.premium_ratio.total) * 100}%`
                                }}
                              />
                            </div>
                            <div className="text-xs text-muted-foreground mt-4">
                              Total: {analytics.premium_ratio.total} usuarios
                            </div>
                          </div>
                        ) : (
                          <p className="text-muted-foreground text-sm">No hay datos</p>
                        )}
                      </div>
                    </div>

                    {/* Top Events */}
                    <div className="border rounded-lg p-4">
                      <h3 className="font-semibold mb-4">Top 10 Eventos</h3>
                      {analytics.top_events && analytics.top_events.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b">
                                <th className="text-left py-2">Evento</th>
                                <th className="text-right py-2">Registros</th>
                                <th className="text-right py-2">Capacidad</th>
                                <th className="text-right py-2">%</th>
                              </tr>
                            </thead>
                            <tbody>
                              {analytics.top_events.map((evt, idx) => (
                                <tr key={idx} className="border-b text-xs">
                                  <td className="py-2 truncate">{evt.title}</td>
                                  <td className="text-right py-2">{evt.registrations}</td>
                                  <td className="text-right py-2">{evt.capacity || "∞"}</td>
                                  <td className="text-right py-2">
                                    {evt.capacity ? Math.round((evt.registrations / evt.capacity) * 100) : "-"}%
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="text-muted-foreground text-sm">No hay datos</p>
                      )}
                    </div>

                    {/* Users Timeline */}
                    <div className="border rounded-lg p-4">
                      <h3 className="font-semibold mb-4">Timeline de Usuarios (Últimos 30 días)</h3>
                      {analytics.users_timeline && analytics.users_timeline.length > 0 ? (
                        <div className="space-y-2">
                          {analytics.users_timeline.map((item) => (
                            <div key={item.date} className="flex items-center justify-between text-sm">
                              <span className="text-muted-foreground">{item.date}</span>
                              <div className="flex items-center gap-2">
                                <div className="w-32 bg-muted rounded-full h-2">
                                  <div
                                    className="bg-purple-500 h-2 rounded-full"
                                    style={{
                                      width: `${(item.count / Math.max(...analytics.users_timeline.map(u => u.count))) * 100}%`
                                    }}
                                  />
                                </div>
                                <span className="font-medium w-8 text-right">{item.count}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted-foreground text-sm">No hay datos</p>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="categories" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Gestión de Categorías</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Create Category */}
                  <div className="border rounded-lg p-4 bg-muted/30">
                    <h3 className="font-semibold mb-4">Crear Nueva Categoría</h3>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Nombre de la categoría"
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleCreateCategory();
                          }
                        }}
                        className="flex-1 px-3 py-2 border rounded-md bg-background"
                      />
                      <Button onClick={handleCreateCategory}>Crear</Button>
                    </div>
                  </div>

                  {/* Categories List */}
                  {loading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin" />
                    </div>
                  ) : categories && categories.length > 0 ? (
                    <div className="space-y-2">
                      {categories.map((cat) => (
                        <div key={cat.id} className="flex items-center justify-between p-3 border rounded-lg bg-muted/50 hover:bg-muted">
                          <div>
                            <p className="font-medium">{cat.name}</p>
                            <p className="text-xs text-muted-foreground">ID: {cat.id}</p>
                          </div>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDeleteCategory(cat.id, cat.name)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-center py-8">No hay categorías</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="moderation" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Eventos Pendientes de Moderación</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin" />
                  </div>
                ) : pendingEvents.length > 0 ? (
                  <div className="space-y-4">
                    {pendingEvents.map((event) => (
                      <div key={event.event_id} className="border rounded-lg p-4 space-y-3">
                        <div>
                          <h3 className="font-semibold">{event.title}</h3>
                          <p className="text-sm text-muted-foreground">{event.description}</p>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <div>
                            <p className="text-muted-foreground">Organizador: {event.organizer?.name}</p>
                            <p className="text-xs text-muted-foreground">Creado: {new Date(event.created_at).toLocaleDateString('es-ES')}</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => handleModerateEvent(event.event_id, 'approved', 'Aprobado por admin')}
                          >
                            ✓ Aprobar
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => {
                              const reason = prompt("Razón del rechazo:");
                              if (reason) {
                                handleModerateEvent(event.event_id, 'rejected', reason);
                              }
                            }}
                          >
                            ✕ Rechazar
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">No hay eventos pendientes de moderación</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="revenue" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Análisis de Ingresos y Suscripciones</CardTitle>
              </CardHeader>
              <CardContent>
                {loading || !subscriptionAnalytics ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin" />
                  </div>
                ) : (
                  <div className="space-y-8">
                    {/* Key Metrics */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="border rounded-lg p-4">
                        <p className="text-sm text-muted-foreground mb-2">MRR Estimado</p>
                        <p className="text-3xl font-bold text-green-600">${subscriptionAnalytics.mrr}</p>
                        <p className="text-xs text-muted-foreground mt-2">{subscriptionAnalytics.premium_users} usuarios premium @ $9.99/mes</p>
                      </div>
                      <div className="border rounded-lg p-4">
                        <p className="text-sm text-muted-foreground mb-2">Suscripciones Activas</p>
                        <p className="text-3xl font-bold text-blue-600">{subscriptionAnalytics.active_subscriptions}</p>
                      </div>
                      <div className="border rounded-lg p-4">
                        <p className="text-sm text-muted-foreground mb-2">Total Premium</p>
                        <p className="text-3xl font-bold text-amber-600">{subscriptionAnalytics.premium_users}</p>
                      </div>
                    </div>

                    {/* Revenue Timeline */}
                    <div className="border rounded-lg p-4">
                      <h3 className="font-semibold mb-4">Timeline de Ingresos (Últimos 30 días)</h3>
                      {subscriptionAnalytics.revenue_timeline && subscriptionAnalytics.revenue_timeline.length > 0 ? (
                        <div className="space-y-3">
                          {subscriptionAnalytics.revenue_timeline.map((item) => (
                            <div key={item.date} className="flex items-center justify-between text-sm">
                              <span className="text-muted-foreground w-24">{item.date}</span>
                              <div className="flex-1 mx-4">
                                <div className="w-full bg-muted rounded-full h-2">
                                  <div
                                    className="bg-green-500 h-2 rounded-full"
                                    style={{
                                      width: `${(item.new_subscriptions / Math.max(...subscriptionAnalytics.revenue_timeline.map(r => r.new_subscriptions))) * 100}%`
                                    }}
                                  />
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="font-semibold">{item.new_subscriptions} nuevas</p>
                                <p className="text-xs text-muted-foreground">${item.daily_revenue.toFixed(2)}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted-foreground text-center">No hay datos de ingresos</p>
                      )}
                    </div>

                    {/* Subscription Status Summary */}
                    <div className="border rounded-lg p-4">
                      <h3 className="font-semibold mb-4">Estado de Suscripciones</h3>
                      {subscriptionAnalytics.subscription_statuses && subscriptionAnalytics.subscription_statuses.length > 0 ? (
                        <div className="space-y-2">
                          {subscriptionAnalytics.subscription_statuses.map((status) => (
                            <div key={status._id} className="flex items-center justify-between p-2 bg-muted rounded">
                              <span className="capitalize">{status._id || 'Sin estado'}</span>
                              <span className="font-semibold">{status.count}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted-foreground">No hay datos de suscripción</p>
                      )}
                    </div>

                    {/* Premium Users List */}
                    <div className="border rounded-lg p-4">
                      <h3 className="font-semibold mb-4">Usuarios Premium</h3>
                      {premiumUsers.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b">
                                <th className="text-left py-2">Usuario</th>
                                <th className="text-left py-2">Email</th>
                                <th className="text-left py-2">Estado</th>
                                <th className="text-left py-2">Desde</th>
                              </tr>
                            </thead>
                            <tbody>
                              {premiumUsers.slice(0, 10).map((u) => (
                                <tr key={u.user_id} className="border-b">
                                  <td className="py-2">{u.name}</td>
                                  <td className="py-2 text-xs text-muted-foreground">{u.email}</td>
                                  <td className="py-2"><span className="bg-amber-100 dark:bg-amber-900 px-2 py-1 rounded text-xs">{u.subscription_status || "active"}</span></td>
                                  <td className="py-2 text-xs">{new Date(u.created_at).toLocaleDateString('es-ES')}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="text-muted-foreground">No hay usuarios premium</p>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="settings" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Configuración Global de la Plataforma</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6 max-w-2xl">
                  {/* Commission Rate */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Porcentaje de Comisión (%)</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={editingSettings.commission_rate || "0"}
                      onChange={(e) => setEditingSettings({...editingSettings, commission_rate: e.target.value})}
                      className="w-full px-3 py-2 border rounded-md bg-background"
                    />
                    <p className="text-xs text-muted-foreground">La comisión aplicada a cada transacción de eventos premium</p>
                  </div>

                  {/* Max Events Per User */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Máximo de Eventos por Usuario</label>
                    <input
                      type="text"
                      value={editingSettings.max_events_per_user || "unlimited"}
                      onChange={(e) => setEditingSettings({...editingSettings, max_events_per_user: e.target.value})}
                      placeholder="unlimited o número"
                      className="w-full px-3 py-2 border rounded-md bg-background"
                    />
                    <p className="text-xs text-muted-foreground">Escribe 'unlimited' para sin límite o un número</p>
                  </div>

                  {/* Terms of Service */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Términos de Servicio</label>
                    <textarea
                      value={editingSettings.terms_of_service || ""}
                      onChange={(e) => setEditingSettings({...editingSettings, terms_of_service: e.target.value})}
                      className="w-full px-3 py-2 border rounded-md bg-background h-32"
                    />
                  </div>

                  {/* Privacy Policy */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Política de Privacidad</label>
                    <textarea
                      value={editingSettings.privacy_policy || ""}
                      onChange={(e) => setEditingSettings({...editingSettings, privacy_policy: e.target.value})}
                      className="w-full px-3 py-2 border rounded-md bg-background h-32"
                    />
                  </div>

                  {/* Save Button */}
                  <div className="flex gap-2">
                    <Button onClick={handleSaveSettings} className="bg-blue-600 hover:bg-blue-700">
                      Guardar Cambios
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setEditingSettings({...platformSettings})}
                    >
                      Cancelar
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Modal de edición de evento. Solo refresca el listado si se guardó;
          al cancelar/cerrar no recarga, así no se pierde la posición del scroll. */}
      <Dialog open={!!editingEventId} onOpenChange={(open) => { if (!open) setEditingEventId(null); }}>
        <DialogContent
          className="w-[calc(100%-1.5rem)] max-w-6xl max-h-[90vh] overflow-y-auto"
          onInteractOutside={(e) => {
            // No cerrar el modal al clicar en el desplegable de Google Places
            const target = e.detail?.originalEvent?.target;
            if (target?.closest?.(".pac-container")) e.preventDefault();
          }}
        >
          <DialogHeader>
            <DialogTitle>Editar Evento</DialogTitle>
          </DialogHeader>
          {editingEventId && (
            <EventForm
              editMode
              asModal
              eventId={editingEventId}
              onClose={(saved) => { setEditingEventId(null); if (saved) fetchAdminData(true); }}
            />
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

// ==================== APP ROUTER ====================

const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment for session_id SYNCHRONOUSLY during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Prefijo de idioma obligatorio: si la URL no empieza por /es o /ca, redirige al idioma por defecto
  const seg = location.pathname.split("/")[1];
  if (!LANGS.includes(seg)) {
    const rest = location.pathname === "/" ? "" : location.pathname;
    return <RouterNavigate to={`/${DEFAULT_LANG}${rest}${location.search}`} replace />;
  }

  return (
    <Routes>
      {/* Rutas públicas (bajo /:lang) */}
      <Route path="/:lang" element={<PublicEventsPage />} />
      <Route path="/:lang/eventos" element={<PublicEventsPage />} />
      <Route path="/:lang/eventos/:townSlug" element={<PublicEventsPage />} />
      <Route path="/:lang/mapa" element={<PublicMapPage />} />
      <Route path="/:lang/evento/:eventId" element={<EventDetailPage />} />
      <Route path="/:lang/organizador" element={<OrganizerLandingPage />} />
      <Route path="/:lang/organizador/:userId" element={<OrganizerProfilePage />} />
      <Route path="/:lang/premium" element={<PremiumPage />} />

      {/* Rutas con login */}
      <Route path="/:lang/favoritos" element={<ProtectedRoute><FavoritesPage /></ProtectedRoute>} />
      <Route path="/:lang/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/:lang/create-event" element={<ProtectedRoute><EventForm /></ProtectedRoute>} />
      <Route path="/:lang/edit-event/:eventId" element={<ProtectedRoute><EventForm editMode /></ProtectedRoute>} />
      <Route path="/:lang/my-events" element={<ProtectedRoute><MyEventsPage /></ProtectedRoute>} />
      <Route path="/:lang/my-registrations" element={<ProtectedRoute><MyRegistrationsPage /></ProtectedRoute>} />
      <Route path="/:lang/event/:eventId/attendees" element={<ProtectedRoute><AttendeesPage /></ProtectedRoute>} />
      <Route path="/:lang/admin" element={<ProtectedRoute><AdminPanel /></ProtectedRoute>} />

      {/* Fallback dentro del idioma */}
      <Route path="*" element={<RouterNavigate to={`/${DEFAULT_LANG}`} replace />} />
    </Routes>
  );
};

// ==================== MAIN APP ====================

function App() {
  return (
    <HelmetProvider>
    <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY} libraries={GOOGLE_LIBRARIES}>
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <div className="App min-h-screen bg-background">
              <Header />
              <main>
                <AppRouter />
              </main>
              <Toaster position="top-right" richColors />
            </div>
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </LoadScript>
    </HelmetProvider>
  );
}

export default App;
